import React, { useEffect, useMemo, useState } from "react";
import "./App.css";
import axios from "axios";
import { Calendar } from "./components/ui/calendar";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Slider } from "./components/ui/slider";
import { Card, CardContent } from "./components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Textarea } from "./components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { BarChart3, BrainCircuit, Sparkles } from "lucide-react";
import { useI18n } from "./i18n";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Extracted components
import { useSession } from "./hooks/useSession";
import { Login } from "./components/Login";
import { AiAnalyticsModule } from "./components/modules/AiAnalyticsModule";
import { HelpModule } from "./components/modules/HelpModule";
import { SupportModule } from "./components/modules/SupportModule";
import { StatusModule } from "./components/modules/StatusModule";
import { AuditModule } from "./components/modules/AuditModule";
import { WebhooksModule } from "./components/modules/WebhooksModule";
import KasseModule from "./components/modules/KasseModule";
import ShippingModule from "./components/modules/ShippingModule";
import AutomationModule from "./components/modules/AutomationModule";
import SettingsModule from "./components/modules/SettingsModule";
import DashboardModule from "./components/modules/DashboardModule";
import LiveViewModule from "./components/modules/LiveViewModule";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard({ session }) {
  const { lang, setLang, t } = useI18n();
  const [me, setMe] = useState({ code: "", region: "", timezone: "", camera_ids: [], user: null });
  const [userRole, setUserRole] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard"); // Start with dashboard instead of analyse
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [threshold, setThreshold] = useState(1);
  const [gap, setGap] = useState(3);
  const [camerasText, setCamerasText] = useState("");
  const [plateFilter, setPlateFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [pairs, setPairs] = useState([]);
  const [rowsSight, setRowsSight] = useState([]);
  const [xApiKey, setXApiKey] = useState("");
  const [positives, setPositives] = useState([]);
  const [positivesInput, setPositivesInput] = useState("");
  const [hasXKey, setHasXKey] = useState(false);
  const [savedCameras, setSavedCameras] = useState([]);
  const [preview, setPreview] = useState({ open: false, entry: "", exit: "", plate: "" });
  const [savingPrefs, setSavingPrefs] = useState(false);
  
  // Alarm State
  const [alarmState, setAlarmState] = useState(() => {
    // Load from localStorage on init
    const saved = localStorage.getItem('alarmState');
    return saved || 'disarmed';
  });

  // Kasse state (Phase 4)
  const [kasseItems, setKasseItems] = useState([]);
  const [kasseTotalAmount, setKasseTotalAmount] = useState(0);
  const [kasseCustomerName, setKasseCustomerName] = useState("");
  const [kasseNewItemName, setKasseNewItemName] = useState("");
  const [kasseNewItemPrice, setKasseNewItemPrice] = useState("");
  const [kasseNewItemQuantity, setKasseNewItemQuantity] = useState(1);

  // Responsive breakpoints (Phase 5)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;

  // Kassenmodul Einstellungen
  const [kassenUID, setKassenUID] = useState("");
  const [kassenCameraID, setKassenCameraID] = useState("");
  const [savedKassenUID, setSavedKassenUID] = useState("");
  const [savedKassenCameraID, setSavedKassenCameraID] = useState("");
  const [kasseEvents, setKasseEvents] = useState([]); // For events history
  const [kasseSearch, setKasseSearch] = useState(""); // Search query
  const [kasseSortBy, setKasseSortBy] = useState("timestamp"); // Sort column
  const [kasseSortOrder, setKasseSortOrder] = useState("desc"); // Sort order
  const [selectedKasseReceipt, setSelectedKasseReceipt] = useState(null); // For receipt modal
  
  // Shipping Module States
  const [shippingUID, setShippingUID] = useState("");
  const [shippingCameraID, setShippingCameraID] = useState("");
  const [savedShippingUID, setSavedShippingUID] = useState("");
  const [savedShippingCameraID, setSavedShippingCameraID] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [shippingContent, setShippingContent] = useState("");
  const [currentTrackingNumber, setCurrentTrackingNumber] = useState("");
  const [shippingEvents, setShippingEvents] = useState([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null); // For modal view
  const [selectedEmailEvent, setSelectedEmailEvent] = useState(null); // For email modal
  const [shippingSearch, setShippingSearch] = useState(""); // Search query
  const [shippingSortBy, setShippingSortBy] = useState("timestamp"); // Sort column
  const [shippingSortOrder, setShippingSortOrder] = useState("desc"); // Sort order

  // Shelly Integration States
  const [shellyServer, setShellyServer] = useState("");
  const [shellyAuthKey, setShellyAuthKey] = useState("");
  const [shellyDevices, setShellyDevices] = useState([]); // [{id: "", name: ""}]
  const [savedShellyServer, setSavedShellyServer] = useState("");
  const [savedShellyAuthKey, setSavedShellyAuthKey] = useState(false);
  const [savedShellyDevices, setSavedShellyDevices] = useState([]); // [{id: "...", name: "..."}]

  // Live View Settings — user-supplied embedded iframe snippets
  const [liveViews, setLiveViews] = useState([]);          // [{name: "", embed: ""}]
  const [savedLiveViews, setSavedLiveViews] = useState([]); // [{name: "...", embed: "..."}]

  // Admin console state
  const [newUser, setNewUser] = useState({ email: "", password: "", role: "user", support_enabled: false });
  const [deleteEmail, setDeleteEmail] = useState("");
  const [userList, setUserList] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  
  // Support module state
  const [supportEnabled, setSupportEnabled] = useState(false);

  // AI Analytics state
  const [aiAnalyticsImage, setAiAnalyticsImage] = useState(null);
  const [aiAnalyticsInstruction, setAiAnalyticsInstruction] = useState("");
  const [aiAnalyticsResult, setAiAnalyticsResult] = useState("");
  const [aiAnalyticsLoading, setAiAnalyticsLoading] = useState(false);
  const [aiAnalyticsEpochTimestamp, setAiAnalyticsEpochTimestamp] = useState(null);
  const [aiAnalyticsAnalyzing, setAiAnalyticsAnalyzing] = useState(false);
  const [aiAnalyticsImageSource, setAiAnalyticsImageSource] = useState(null); // 'api' or 'upload'
  const [aiAnalyticsHelixStatus, setAiAnalyticsHelixStatus] = useState(null); // {success: bool, message: string}
  const [aiAnalyticsHistory, setAiAnalyticsHistory] = useState([]);
  
  // Custom Prompt state
  const [customPromptInput, setCustomPromptInput] = useState("");
  const [customPromptResult, setCustomPromptResult] = useState("");
  const [customPromptLoading, setCustomPromptLoading] = useState(false);
  
  // AI Analytics Settings
  const [aiAnalyticsCameraId, setAiAnalyticsCameraId] = useState("");
  const [aiAnalyticsGeminiKey, setAiAnalyticsGeminiKey] = useState("");
  const [aiAnalyticsGeminiKeySaved, setAiAnalyticsGeminiKeySaved] = useState(false);
  const [aiAnalyticsHelixUid, setAiAnalyticsHelixUid] = useState("");
  const [aiAnalyticsOrgId, setAiAnalyticsOrgId] = useState("");

  // AI Analytics Auto-Refresh (preview image)
  const [aiAutoRefreshEnabled, setAiAutoRefreshEnabled] = useState(false);
  const [aiAutoRefreshMinutes, setAiAutoRefreshMinutes] = useState(5);
  const [aiAutoRefreshPreset, setAiAutoRefreshPreset] = useState(""); // key of preset instruction or empty

  // Automation: LPR Accepted → Shelly Switch1 pulse (10s)
  const [automationLprShellyEnabled, setAutomationLprShellyEnabledRaw] = useState(false);
  const setAutomationLprShellyEnabled = (val) => {
    setAutomationLprShellyEnabledRaw(!!val);
    if (session?.userToken) {
      axios.patch(`${API}/user/settings`, {
        preferences: { automation_lpr_shelly_enabled: !!val }
      }, { headers: session.authHeaders() }).catch(() => {});
    }
  };

  // Automation: LPR Rejected → Shelly Switch2 pulse (10s)
  const [automationLprRejectedShellyEnabled, setAutomationLprRejectedShellyEnabledRaw] = useState(false);
  const setAutomationLprRejectedShellyEnabled = (val) => {
    setAutomationLprRejectedShellyEnabledRaw(!!val);
    if (session?.userToken) {
      axios.patch(`${API}/user/settings`, {
        preferences: { automation_lpr_rejected_shelly_enabled: !!val }
      }, { headers: session.authHeaders() }).catch(() => {});
    }
  };

  // Twilio + Disarmed Call automation
  const [twilioAccountSid, setTwilioAccountSid] = useState("");
  const [twilioAuthToken, setTwilioAuthToken] = useState("");
  const [twilioPhoneNumber, setTwilioPhoneNumber] = useState("");
  const [savedTwilioAccountSid, setSavedTwilioAccountSid] = useState(false);
  const [savedTwilioAuthToken, setSavedTwilioAuthToken] = useState(false);
  const [savedTwilioPhoneNumber, setSavedTwilioPhoneNumber] = useState("");
  const [automationDisarmedCallEnabled, setAutomationDisarmedCallEnabledRaw] = useState(false);
  const setAutomationDisarmedCallEnabled = (val) => {
    setAutomationDisarmedCallEnabledRaw(!!val);
    if (session?.userToken) {
      axios.patch(`${API}/user/settings`, {
        preferences: { automation_disarmed_call_enabled: !!val }
      }, { headers: session.authHeaders() }).catch(() => {});
    }
  };
  const [automationDisarmedCallTo, setAutomationDisarmedCallTo] = useState("");
  const [automationDisarmedCallLanguage, setAutomationDisarmedCallLanguage] = useState("de-DE");
  const [automationDisarmedCallText, setAutomationDisarmedCallText] = useState("");

  // Twilio: Armed Call automation
  const [automationArmedCallEnabled, setAutomationArmedCallEnabledRaw] = useState(false);
  const setAutomationArmedCallEnabled = (val) => {
    setAutomationArmedCallEnabledRaw(!!val);
    if (session?.userToken) {
      axios.patch(`${API}/user/settings`, {
        preferences: { automation_armed_call_enabled: !!val }
      }, { headers: session.authHeaders() }).catch(() => {});
    }
  };
  const [automationArmedCallTo, setAutomationArmedCallTo] = useState("");
  const [automationArmedCallLanguage, setAutomationArmedCallLanguage] = useState("de-DE");
  const [automationArmedCallText, setAutomationArmedCallText] = useState("");

  // VIP plates state
  const [vipPlates, setVipPlates] = useState([]);
  const [vipInput, setVipInput] = useState("");
  const [vipDescription, setVipDescription] = useState("");
  const [syncingVips, setSyncingVips] = useState(false);
  
  // Webhook URL state (editable) - will be populated from webhook config
  const [webhookURL, setWebhookURL] = useState("");

  // Duration statistics state
  const [durationStats, setDurationStats] = useState(null);
  const [loadingDurationStats, setLoadingDurationStats] = useState(false);

  // Webhook system state
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [loadingWebhook, setLoadingWebhook] = useState(false);
  const [webhookNotifications, setWebhookNotifications] = useState([]);
  const [newWebhookToken, setNewWebhookToken] = useState("");
  const [creatingWebhook, setCreatingWebhook] = useState(false);

  // Analysis submenu state
  const [showAnalysisDropdown, setShowAnalysisDropdown] = useState(false);
  const [analysisSubTab, setAnalysisSubTab] = useState('analyse'); // 'analyse', 'status', 'audit', or 'kasse'

  // Cameras state
  const [cameras, setCameras] = useState([]);
  const [loadingCameras, setLoadingCameras] = useState(false);

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditTimeRange, setAuditTimeRange] = useState('24h');
  const [auditPageSize, setAuditPageSize] = useState(100);
  const [auditNextPageToken, setAuditNextPageToken] = useState(null);
  const [auditCustomDays, setAuditCustomDays] = useState(7); // Custom days slider
  const [auditSortField, setAuditSortField] = useState('timestamp');
  const [auditSortDirection, setAuditSortDirection] = useState('desc');
  const [auditSelectedItems, setAuditSelectedItems] = useState(new Set());
  const [auditSelectAll, setAuditSelectAll] = useState(false);

  // Load tenant basics and user preferences
  useEffect(() => {
    const init = async () => {
      if (session.userToken) {
        const loadBasics = async () => {
          try {
            const res = await axios.get(`${API}/me`, { headers: session.authHeaders() });
            setMe(res.data);
            setCamerasText((res.data.camera_ids || []).join(","));
          } catch (e) {
          }
        };
        const loadUserPrefs = async () => {
          if (!session.isUser()) return;
          try {
            const meUser = await axios.get(`${API}/user/me`, { headers: session.authHeaders() });
            const prefs = meUser.data.preferences || {};
            setUserRole(meUser.data.role || "user");
            setSupportEnabled(meUser.data.support_enabled || false);
            if (prefs.camera_ids && prefs.camera_ids.length) {
              setCamerasText(prefs.camera_ids.join(","));
              setSavedCameras(prefs.camera_ids);
            }
            if (prefs.default_threshold != null) setThreshold(prefs.default_threshold);
            if (prefs.default_gap != null) setGap(prefs.default_gap);
            if (prefs.plate_filter != null) setPlateFilter(prefs.plate_filter);
            if (prefs.region || prefs.timezone) {
              setMe(m => ({...m, region: prefs.region || m.region, timezone: prefs.timezone || m.timezone }));
            }
            setHasXKey(!!prefs.x_api_key);
            
            // Kassenmodul Einstellungen laden
            if (prefs.kassen_uid) {
              setSavedKassenUID(prefs.kassen_uid);
            }
            if (prefs.kassen_camera_id) {
              setSavedKassenCameraID(prefs.kassen_camera_id);
            }
            if (prefs.shipping_uid) {
              setSavedShippingUID(prefs.shipping_uid);
            }
            if (prefs.shipping_camera_id) {
              setSavedShippingCameraID(prefs.shipping_camera_id);
            }
            
            // AI Analytics Einstellungen laden
            if (prefs.ai_analytics_camera_id) {
              setAiAnalyticsCameraId(prefs.ai_analytics_camera_id);
            }
            if (prefs.ai_analytics_gemini_key) {
              setAiAnalyticsGeminiKeySaved(true);
            }
            if (prefs.ai_analytics_helix_uid) {
              setAiAnalyticsHelixUid(prefs.ai_analytics_helix_uid);
            }
            if (prefs.ai_analytics_org_id) {
              setAiAnalyticsOrgId(prefs.ai_analytics_org_id);
            }
            if (typeof prefs.ai_auto_refresh_enabled === 'boolean') {
              setAiAutoRefreshEnabled(prefs.ai_auto_refresh_enabled);
            }
            if (typeof prefs.ai_auto_refresh_minutes === 'number' && prefs.ai_auto_refresh_minutes > 0) {
              setAiAutoRefreshMinutes(prefs.ai_auto_refresh_minutes);
            }
            if (typeof prefs.ai_auto_refresh_preset === 'string') {
              setAiAutoRefreshPreset(prefs.ai_auto_refresh_preset);
            }
            if (typeof prefs.automation_lpr_shelly_enabled === 'boolean') {
              setAutomationLprShellyEnabledRaw(prefs.automation_lpr_shelly_enabled);
            }
            if (typeof prefs.automation_lpr_rejected_shelly_enabled === 'boolean') {
              setAutomationLprRejectedShellyEnabledRaw(prefs.automation_lpr_rejected_shelly_enabled);
            }
            // Twilio + disarmed call settings
            if (prefs.twilio_account_sid) setSavedTwilioAccountSid(true);
            if (prefs.twilio_auth_token) setSavedTwilioAuthToken(true);
            if (prefs.twilio_phone_number) setSavedTwilioPhoneNumber(prefs.twilio_phone_number);
            if (typeof prefs.automation_disarmed_call_enabled === 'boolean') {
              setAutomationDisarmedCallEnabledRaw(prefs.automation_disarmed_call_enabled);
            }
            if (prefs.automation_disarmed_call_to) setAutomationDisarmedCallTo(prefs.automation_disarmed_call_to);
            if (prefs.automation_disarmed_call_language) setAutomationDisarmedCallLanguage(prefs.automation_disarmed_call_language);
            if (prefs.automation_disarmed_call_text) setAutomationDisarmedCallText(prefs.automation_disarmed_call_text);
            if (typeof prefs.automation_armed_call_enabled === 'boolean') {
              setAutomationArmedCallEnabledRaw(prefs.automation_armed_call_enabled);
            }
            if (prefs.automation_armed_call_to) setAutomationArmedCallTo(prefs.automation_armed_call_to);
            if (prefs.automation_armed_call_language) setAutomationArmedCallLanguage(prefs.automation_armed_call_language);
            if (prefs.automation_armed_call_text) setAutomationArmedCallText(prefs.automation_armed_call_text);
            
            // Shelly Einstellungen laden
            if (prefs.shelly_server) {
              setSavedShellyServer(prefs.shelly_server);
            }
            if (prefs.shelly_auth_key) {
              setSavedShellyAuthKey(true);
            }
            // Dynamic shelly devices: prefer array, fallback to legacy fields
            if (prefs.shelly_devices && Array.isArray(prefs.shelly_devices)) {
              setSavedShellyDevices(prefs.shelly_devices);
              setShellyDevices(prefs.shelly_devices.map(d => ({ id: d.id || '', name: d.name || '' })));
            } else {
              // Migrate legacy device1/device2 fields
              const legacyDevices = [];
              if (prefs.shelly_device_id_1) {
                legacyDevices.push({ id: prefs.shelly_device_id_1, name: prefs.shelly_device_name_1 || '' });
              }
              if (prefs.shelly_device_id_2) {
                legacyDevices.push({ id: prefs.shelly_device_id_2, name: prefs.shelly_device_name_2 || '' });
              }
              if (legacyDevices.length) {
                setSavedShellyDevices(legacyDevices);
                setShellyDevices(legacyDevices.map(d => ({ id: d.id, name: d.name })));
              }
            }

            // Live View entries
            if (Array.isArray(prefs.live_views)) {
              const clean = prefs.live_views.map(lv => ({
                name: (lv && lv.name) || '',
                embed: (lv && lv.embed) || '',
              }));
              setSavedLiveViews(clean);
              setLiveViews(clean.map(lv => ({ name: lv.name, embed: lv.embed })));
            }
          } catch (e) {
          }
        };
        const loadPosList = async () => {
          await refreshPositives();
        };
        const loadVipList = async () => {
          await refreshVipPlates();
        };
        
        await loadBasics();
        await loadUserPrefs();
        await loadPosList();
        await loadVipList();
        loadWebhookConfig();
        
        // Request notification permission
        if (Notification.permission === 'default') {
          Notification.requestPermission();
        }
        
        // Start polling for webhook notifications
        const notificationInterval = setInterval(pollWebhookNotifications, 5000);
        return () => clearInterval(notificationInterval);
      } else if (session.token) {
        setActiveTab('dashboard');
      }
    };
    init();
  }, [session.userToken, session.token]);

  // Auto-load cameras when switching to status dashboard
  useEffect(() => {
    if (activeTab === 'analyse' && analysisSubTab === 'status' && hasXKey && cameras.length === 0 && !loadingCameras) {
      loadCameras();
    }
  }, [activeTab, analysisSubTab, hasXKey]);

  // Auto-load audit logs when switching to audit dashboard
  useEffect(() => {
    if (activeTab === 'analyse' && analysisSubTab === 'audit' && hasXKey && !auditLogs && !loadingAudit) {
      loadAuditLogs();
    }
  }, [activeTab, analysisSubTab, hasXKey]);

  // Auto-load webhook events when switching to webhook dashboard
  useEffect(() => {
    if (activeTab === 'analyse' && analysisSubTab === 'webhook-events' && session.userToken) {
      loadWebhookEvents();
    }
  }, [activeTab, analysisSubTab, session.userToken]);

  const isAdmin = session.isUser() && userRole === 'admin';

  useEffect(() => {
    if (isAdmin) setActiveTab('admin'); 
    // Für normale User: Dashboard als Startseite beibehalten (nicht auf 'analyse' zwingen)
  }, [isAdmin]);

  // Auto-load user list when admin view opens
  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  // Nach Login automatisch zum Dashboard navigieren
  useEffect(() => {
    if (session.isLoggedIn() && activeTab !== 'dashboard') {
      setActiveTab('dashboard');
    }
  }, [session.userToken, session.token]);

  // Load shipping events when shipping tab is opened
  useEffect(() => {
    if (analysisSubTab === 'shipping') {
      loadShippingEvents();
    }
  }, [analysisSubTab]);



  // Monitor webhook events for alarm state changes
  useEffect(() => {
    if (webhookEvents && webhookEvents.length > 0) {
      // Find the most recent alarm event - check for:
      // 1. alarm_site_state_changed event type (original Verkada format)
      // 2. Direct "armed" or "disarmed" event types
      const alarmEvents = webhookEvents.filter(event => {
        const eventType = event.event_type?.toLowerCase();
        return eventType === 'alarm_site_state_changed' || 
               eventType === 'armed' || 
               eventType === 'disarmed';
      });
      
      if (alarmEvents.length > 0) {
        // Get the most recent one (first in array as they're sorted by time)
        const latestAlarmEvent = alarmEvents[0];
        const eventType = latestAlarmEvent.event_type?.toLowerCase();
        
        let newState = 'disarmed';
        
        try {
          // Check if event_type is directly "armed" or "disarmed"
          if (eventType === 'armed') {
            newState = 'armed';
          } else if (eventType === 'disarmed') {
            newState = 'disarmed';
          } else {
            // For alarm_site_state_changed, check multiple payload structures:
            const payload = typeof latestAlarmEvent.payload === 'string' 
              ? JSON.parse(latestAlarmEvent.payload) 
              : latestAlarmEvent.payload;
            
            // Check nested structure: payload.data.event_type (Verkada format)
            const nestedEventType = payload?.data?.event_type?.toLowerCase();
            if (nestedEventType === 'armed' || nestedEventType === 'disarmed') {
              newState = nestedEventType;
            } else {
              // Fallback to payload.state
              newState = payload?.state?.toLowerCase() || 'disarmed';
            }
          }
          
          // Update state if changed
          if (newState !== alarmState) {
            const previousState = alarmState;
            setAlarmState(newState);
            localStorage.setItem('alarmState', newState);
            
            // Play sound if changing TO armed
            if (newState === 'armed' && previousState !== 'armed') {
              playAlarmSound();
            }
          }
        } catch (e) {
        }
      }
    }
  }, [webhookEvents]);

  // Auto-refresh webhook events - faster when Webhooks module is active
  useEffect(() => {
    if (session.userToken && !isAdmin) {
      // 2 seconds when Webhooks tab active, 10 seconds otherwise
      const pollInterval = activeTab === 'webhooks' ? 2000 : 10000;
      
      const interval = setInterval(() => {
        loadWebhookEvents();
      }, pollInterval);
      
      return () => clearInterval(interval);
    }
  }, [session.userToken, isAdmin, activeTab]);
  
  // Function to play alarm sound
  const playAlarmSound = () => {
    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // 800 Hz beep
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
    }
  };


  // Load kasse events when kasse tab is opened
  useEffect(() => {
    if (analysisSubTab === 'kasse') {
      loadKasseEvents();
    }
  }, [analysisSubTab]);

  // Load AI Analytics history when tab is opened
  useEffect(() => {
    if (analysisSubTab === 'ai-analytics') {
      loadAiAnalyticsHistory();
    }
  }, [analysisSubTab]);

  const loadAiAnalyticsHistory = async () => {
    try {
      const response = await axios.get(`${API}/ai-analytics/history?limit=1000`, {
        headers: session.authHeaders()
      });
      if (response.data.success) {
        setAiAnalyticsHistory(response.data.history || []);
      }
    } catch (error) {
    }
  };

  // Mapping of preset keys to actual instruction texts (per language)
  const AI_PRESETS = {
    cat: { de: 'Ist eine Katze zu sehen? Antworte nur mit true oder false.', en: 'Is there a cat visible? Answer with true or false only.' },
    tree: { de: 'Ist ein Baum zu sehen? Antworte nur mit true oder false.', en: 'Is there a tree visible? Answer with true or false only.' },
    snow: { de: 'Liegt Schnee? Antworte nur mit true oder false.', en: 'Is there snow? Answer with true or false only.' },
    sun: { de: 'Scheint die Sonne? Antworte nur mit true oder false.', en: 'Is the sun shining? Answer with true or false only.' },
  };

  // Fetch AI Analytics preview image (used for manual + auto-refresh)
  const fetchAiPreviewImage = async () => {
    if (!aiAnalyticsCameraId || !session?.userToken) return null;
    try {
      const res = await axios.get(`${API}/ai-analytics/latest-image`, {
        headers: session.authHeaders(),
        timeout: 30000
      });
      if (res.data?.success && res.data.image) {
        setAiAnalyticsImage(res.data.image);
        const epoch = res.data.epoch_timestamp || Date.now();
        setAiAnalyticsEpochTimestamp(epoch);
        setAiAnalyticsImageSource('api');
        return { image: res.data.image, epoch };
      }
    } catch (_) {}
    return null;
  };

  // Run AI analyze with given image + instruction (used in auto-refresh)
  const runAiAnalyze = async (imageData, epoch, instruction) => {
    if (!imageData || !instruction || !session?.userToken) return;
    try {
      const res = await axios.post(`${API}/ai-analytics/analyze`, {
        base64_image: imageData,
        instruction: instruction,
        epoch_timestamp: epoch
      }, { headers: session.authHeaders() });
      if (res.data?.success) {
        const result = res.data.result;
        setAiAnalyticsResult(result);
        loadAiAnalyticsHistory();

        // Auto-send Helix Event if result is true/false and Helix is configured
        if ((result === 'true' || result === 'false') &&
            aiAnalyticsHelixUid && aiAnalyticsCameraId && epoch && aiAnalyticsOrgId) {
          try {
            const helixRes = await axios.post(`${API}/ai-analytics/helix-event`, {
              result: result,
              instruction: instruction,
              camera_id: aiAnalyticsCameraId,
              event_type_uid: aiAnalyticsHelixUid,
              org_id: aiAnalyticsOrgId,
              time_ms: epoch,
              flagged: result === 'true'
            }, { headers: session.authHeaders() });
            setAiAnalyticsHelixStatus(helixRes.data?.success ? {
              success: true,
              message: lang === 'de' ? '✅ Helix Event erfolgreich gesendet!' : '✅ Helix Event sent successfully!'
            } : {
              success: false,
              message: lang === 'de' ? `❌ Helix Event fehlgeschlagen: ${helixRes.data?.message || ''}` : `❌ Helix Event failed: ${helixRes.data?.message || ''}`
            });
          } catch (helixErr) {
            setAiAnalyticsHelixStatus({
              success: false,
              message: lang === 'de' ? `❌ Helix Event Fehler: ${helixErr.message}` : `❌ Helix Event error: ${helixErr.message}`
            });
          }
        }
      }
    } catch (_) {}
  };

  // Auto-refresh interval (runs in background regardless of which module is open)
  useEffect(() => {
    if (!aiAutoRefreshEnabled) return;
    const mins = Number(aiAutoRefreshMinutes);
    if (!mins || mins <= 0) return;
    if (!session?.userToken || !aiAnalyticsCameraId) return;
    const ms = mins * 60 * 1000;
    const tick = async () => {
      const result = await fetchAiPreviewImage();
      if (result && aiAutoRefreshPreset && AI_PRESETS[aiAutoRefreshPreset]) {
        const instruction = AI_PRESETS[aiAutoRefreshPreset][lang] || AI_PRESETS[aiAutoRefreshPreset].en;
        await runAiAnalyze(result.image, result.epoch, instruction);
      }
    };
    const intervalId = setInterval(tick, ms);
    tick(); // immediate first run on activation
    return () => clearInterval(intervalId);
  }, [aiAutoRefreshEnabled, aiAutoRefreshMinutes, aiAutoRefreshPreset, session?.userToken, aiAnalyticsCameraId, lang]);


  // Load shipping events
  const loadShippingEvents = async () => {
    try {
      const response = await axios.get(`${API}/user/shipping-events`, {
        headers: { 'X-User-Session-Token': session.userToken }
      });
      if (response.data.success) {
        setShippingEvents(response.data.events || []);
      }
    } catch (error) {
    }
  };

  const loadKasseEvents = async () => {
    try {
      const response = await axios.get(`${API}/user/kasse-events`, {
        headers: { 'X-User-Session-Token': session.userToken }
      });
      if (response.data.success) {
        setKasseEvents(response.data.events || []);
        
        // Load thumbnails for events that don't have them yet
        const eventsWithoutThumbnails = response.data.events.filter(e => !e.thumbnail && e.camera_id && e.epoch_timestamp);
        for (const event of eventsWithoutThumbnails) {
          try {
            await new Promise(resolve => setTimeout(resolve, 3000)); // Wait 3 seconds
            const thumbResponse = await axios.get(`${API}/kasse/thumbnail`, {
              params: {
                camera_id: event.camera_id,
                timestamp_ms: event.epoch_timestamp
              },
              headers: { 'X-User-Session-Token': session.userToken }
            });
            if (thumbResponse.data.success && thumbResponse.data.thumbnail) {
              // Update the event in state
              setKasseEvents(prev => prev.map(e => 
                e.epoch_timestamp === event.epoch_timestamp && e.camera_id === event.camera_id
                  ? { ...e, thumbnail: thumbResponse.data.thumbnail }
                  : e
              ));
            }
          } catch (thumbError) {
          }
        }
      }
    } catch (error) {
    }
  };


  const refreshPositives = async () => {
    try {
      const res = await axios.get(`${API}/user/positives`, { headers: session.authHeaders() });
      setPositives(res.data || []);
    } catch (e) {
    }
  };

  // VIP plates functions
  const refreshVipPlates = async () => {
    try {
      const res = await axios.get(`${API}/user/vips`, { headers: session.authHeaders() });
      setVipPlates(res.data || []);
    } catch (e) {
    }
  };

  // Cameras functions
  const loadCameras = async (withThumbnails = true) => {
    setLoadingCameras(true);
    try {
      const url = withThumbnails ? `${API}/cameras?include_thumbnails=true` : `${API}/cameras`;
      const res = await axios.get(url, { headers: session.authHeaders() });
      setCameras(res.data || []);
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Fehler beim Laden der Kameras";
      alert(`Kamera-Abruf Fehler: ${errorMsg}`);
    } finally {
      setLoadingCameras(false);
    }
  };

  // Audit logs functions
  const loadAuditLogs = async (timeRange = auditTimeRange, pageSize = auditPageSize, nextPageToken = null) => {
    setLoadingAudit(true);
    try {
      // Calculate time range
      const endTime = Math.floor(Date.now() / 1000);
      let startTime;
      
      switch (timeRange) {
        case '24h':
          startTime = endTime - (24 * 60 * 60); // 24 hours ago
          break;
        case '7d':
          startTime = endTime - (7 * 24 * 60 * 60); // 7 days ago
          break;
        case '30d':
          startTime = endTime - (30 * 24 * 60 * 60); // 30 days ago
          break;
        case 'custom':
          startTime = endTime - (auditCustomDays * 24 * 60 * 60); // Custom days
          break;
        default:
          startTime = endTime - (24 * 60 * 60); // Default to 24 hours
      }

      let params = `start_time=${startTime}&end_time=${endTime}&page_size=${pageSize}&use_processed_timestamp=false`;
      if (nextPageToken) {
        params += `&next_page_token=${nextPageToken}`;
      }

      const res = await axios.get(`${API}/audit/logs?${params}`, { headers: session.authHeaders() });
      setAuditLogs(res.data || null);
      setAuditNextPageToken(res.data?.next_page_token || null);
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Fehler beim Laden der Audit Logs";
      alert(`Audit Logs Fehler: ${errorMsg}`);
      setAuditLogs(null);
    } finally {
      setLoadingAudit(false);
    }
  };

  // Sort audit logs function
  const sortAuditLogs = (field) => {
    const newDirection = auditSortField === field && auditSortDirection === 'asc' ? 'desc' : 'asc';
    setAuditSortField(field);
    setAuditSortDirection(newDirection);

    if (auditLogs && auditLogs.audit_logs) {
      const sortedLogs = [...auditLogs.audit_logs].sort((a, b) => {
        let aValue, bValue;
        
        switch (field) {
          case 'timestamp':
            aValue = new Date(a.timestamp || 0).getTime();
            bValue = new Date(b.timestamp || 0).getTime();
            break;
          case 'user_name':
            aValue = (a.user_name || a.user_email || 'System').toLowerCase();
            bValue = (b.user_name || b.user_email || 'System').toLowerCase();
            break;
          case 'event_description':
            aValue = (a.event_description || a.event_name || 'Unknown').toLowerCase();
            bValue = (b.event_description || b.event_name || 'Unknown').toLowerCase();
            break;
          case 'device_name':
            aValue = (a.devices?.[0]?.device_name || a.devices?.[0]?.device_site_name || 'N/A').toLowerCase();
            bValue = (b.devices?.[0]?.device_name || b.devices?.[0]?.device_site_name || 'N/A').toLowerCase();
            break;
          default:
            aValue = a[field] || '';
            bValue = b[field] || '';
        }

        if (newDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });

      setAuditLogs({...auditLogs, audit_logs: sortedLogs});
    }
  };

  // Audit selection functions
  const handleSelectAll = () => {
    if (!auditLogs?.audit_logs) return;
    
    if (auditSelectAll) {
      setAuditSelectedItems(new Set());
      setAuditSelectAll(false);
    } else {
      const allIds = new Set(auditLogs.audit_logs.map((_, idx) => idx));
      setAuditSelectedItems(allIds);
      setAuditSelectAll(true);
    }
  };

  const handleSelectItem = (idx) => {
    const newSelection = new Set(auditSelectedItems);
    if (newSelection.has(idx)) {
      newSelection.delete(idx);
    } else {
      newSelection.add(idx);
    }
    setAuditSelectedItems(newSelection);
    setAuditSelectAll(newSelection.size === auditLogs?.audit_logs?.length);
  };

  const clearSelection = () => {
    setAuditSelectedItems(new Set());
    setAuditSelectAll(false);
  };

  const exportSelected = () => {
    if (!auditLogs?.audit_logs || auditSelectedItems.size === 0) return;
    
    const selectedData = Array.from(auditSelectedItems).map(idx => auditLogs.audit_logs[idx]).filter(Boolean);
    
    const csvContent = [
      [t('audit_timestamp'), t('audit_user'), t('audit_event'), t('audit_device'), t('audit_ip_address')].join(','),
      ...selectedData.map(log => [
        log.timestamp ? formatTimestampWithTimezone(log.timestamp) : 'N/A',
        log.user_name || log.user_email || 'System',
        log.event_description || log.event_name || 'Unknown',
        log.devices?.[0]?.device_name || 'N/A',
        log.ip_address || 'N/A'
      ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `audit_logs_selected_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Format timestamp beautifully (ISO format from Verkada)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    const locale = lang === 'de' ? 'de-DE' : 'en-US';
    
    if (diffHours < 24) {
      const timeStr = date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      const hoursAgo = Math.floor(diffHours);
      const agoText = lang === 'de' ? `vor ${hoursAgo}h` : `${hoursAgo}h ago`;
      return `${timeStr} (${agoText})`;
    } else {
      return date.toLocaleString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  // Load sightings statistics (raw sightings per hour, not parking duration)
  const loadDurationStatistics = async () => {
    if (!selectedDate || !session.userToken) return;
    
    setLoadingDurationStats(true);
    
    try {
      const cams = (camerasText || "").split(/[\n,; ]+/).map(s => s.trim()).filter(Boolean);
      
      
      const response = await axios.get(`${API}/lpr/sightings-statistics`, {
        params: {
          date: dateStr,  // Use same dateStr as raw sightings
          plate_contains: plateFilter || undefined,
          camera_id: cams.length ? cams : undefined
        },
        headers: { 'X-User-Session-Token': session.userToken }
      });
      
      
      // Log vehicle counts per hour for debugging
      response.data.hourly_stats.forEach((stat, index) => {
        if (stat.vehicle_count > 0) {
        }
      });
      
      setDurationStats(response.data);
    } catch (error) {
      setDurationStats(null);
    } finally {
      setLoadingDurationStats(false);
    }
  };

  // Format date for display (convert YYYY-MM-DD to DD-MM-YY)
  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    try {
      const [year, month, day] = dateString.split('-');
      const shortYear = year.slice(-2); // Get last 2 digits of year
      return `${day}-${month}-${shortYear}`;
    } catch (error) {
      return dateString; // Return original if parsing fails
    }
  };

  // Load webhook configuration
  const loadWebhookConfig = async () => {
    if (!session.userToken) return;
    
    setLoadingWebhook(true);
    try {
      const response = await axios.get(`${API}/user/webhook-config`, {
        headers: { 'X-User-Session-Token': session.userToken }
      });
      setWebhookConfig(response.data);
      
      // Update webhookURL with actual token from config
      if (response.data.webhook_token) {
        const baseUrl = window.location.origin;
        setWebhookURL(`${baseUrl}/api/webhook/${response.data.webhook_token}`);
      }
    } catch (error) {
    } finally {
      setLoadingWebhook(false);
    }
  };

  // Load webhook events
  const loadWebhookEvents = async () => {
    if (!session.userToken) return;
    
    try {
      const response = await axios.get(`${API}/user/webhook-events?limit=100`, {
        headers: { 'X-User-Session-Token': session.userToken }
      });
      setWebhookEvents(response.data.events || []);
    } catch (error) {
    }
  };

  // Event categorization and display functions
  const getEventIcon = (eventType, category) => {
    const type = eventType?.toLowerCase() || '';
    
    // Specific event type icons
    if (type.includes('person_of_interest') || type.includes('face')) return '👤';
    if (type.includes('license') || type.includes('plate') || type.includes('vehicle')) return '🚗';
    if (type.includes('door')) return '🚪';
    if (type.includes('motion') || type.includes('person_detected')) return '🏃';
    if (type.includes('intrusion') || type.includes('break')) return '🚨';
    if (type.includes('fire') || type.includes('smoke')) return '🔥';
    if (type.includes('panic') || type.includes('emergency')) return '🆘';
    if (type.includes('temperature') || type.includes('heat')) return '🌡️';
    if (type.includes('air') || type.includes('vape')) return '💨';
    if (type.includes('sound') || type.includes('noise')) return '🔊';
    if (type.includes('camera') || type.includes('tampering')) return '📹';
    if (type.includes('access') || type.includes('card')) return '🔑';
    
    // Category-based icons as fallback
    switch (category) {
      case 'access_control': return '🔐';
      case 'sensor': return '📊';
      case 'alarm': return '🚨';
      case 'camera': return '📹';
      case 'system': return '⚙️';
      default: return '📡';
    }
  };

  const getEventCategoryName = (category) => {
    switch (category) {
      case 'access_control': return 'Zugang';
      case 'sensor': return 'Sensor';
      case 'alarm': return 'Alarm';
      case 'camera': return 'Kamera';
      case 'system': return 'System';
      default: return 'Allgemein';
    }
  };

  const getEventCategoryColor = (category) => {
    switch (category) {
      case 'access_control': 
        return { background: '#f0f9ff', text: '#0369a1', border: '#0ea5e9' };
      case 'sensor':
        return { background: '#f0fdf4', text: '#166534', border: '#10b981' };
      case 'alarm':
        return { background: '#fef2f2', text: '#dc2626', border: '#ef4444' };
      case 'camera':
        return { background: '#fdf4ff', text: '#7c2d12', border: '#a855f7' };
      case 'system':
        return { background: '#f8fafc', text: '#475569', border: '#64748b' };
      default:
        return { background: '#f9fafb', text: '#6b7280', border: '#9ca3af' };
    }
  };

  const getEventTypeEmoji = (eventType, category) => {
    // Prioritize specific event types first
    const eventTypeLower = (eventType || '').toLowerCase();
    const categoryLower = (category || '').toLowerCase();
    
    // Specific event type emojis
    if (eventTypeLower.includes('person_of_interest')) return '👤';
    if (eventTypeLower.includes('motion')) return '🏃';
    if (eventTypeLower.includes('door') || eventTypeLower.includes('access')) return '🚪';
    if (eventTypeLower.includes('keycard')) return '💳';
    if (eventTypeLower.includes('fire') || eventTypeLower.includes('smoke')) return '🔥';
    if (eventTypeLower.includes('panic') || eventTypeLower.includes('emergency')) return '🚨';
    if (eventTypeLower.includes('alarm')) return '⚠️';
    if (eventTypeLower.includes('lpr') || eventTypeLower.includes('license')) return '🚗';
    if (eventTypeLower.includes('temperature')) return '🌡️';
    if (eventTypeLower.includes('air_quality')) return '💨';
    if (eventTypeLower.includes('intrusion')) return '🏠';
    if (eventTypeLower.includes('offline') || eventTypeLower.includes('online')) return '📡';
    if (eventTypeLower.includes('notification')) return '🔔';
    if (eventTypeLower.includes('test') || eventTypeLower.includes('cleanup')) return '🧪';
    
    // Category-based emojis as fallback
    if (categoryLower === 'camera') return '📹';
    if (categoryLower === 'access_control') return '🔐';
    if (categoryLower === 'sensor') return '📊';
    if (categoryLower === 'alarm') return '🚨';
    if (categoryLower === 'system') return '⚙️';
    
    // Default emoji
    return '📋';
  };

  const formatEventType = (eventType) => {
    if (!eventType) return 'Unknown Event';
    
    // Convert snake_case to readable format
    return eventType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Poll for webhook notifications
  const pollWebhookNotifications = async () => {
    if (!session.userToken) return;
    
    try {
      const response = await axios.get(`${API}/user/webhook-notifications`, {
        headers: { 'X-User-Session-Token': session.userToken }
      });
      
      const newNotifications = response.data.notifications || [];
      if (newNotifications.length > 0) {
        setWebhookNotifications(prev => [...newNotifications, ...prev].slice(0, 10));
        // Show browser notification for new webhooks
        if (Notification.permission === 'granted') {
          newNotifications.forEach(notification => {
            new Notification(`${t('webhook_notification_new')}: ${notification.type}`, {
              body: `Event received at ${formatTimestampWithTimezone(notification.timestamp)}`,
              icon: '/favicon.ico'
            });
          });
        }
      }
    } catch (error) {
    }
  };

  const dateStr = useMemo(() => {
    const y = selectedDate.getFullYear();
    const m = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const d = String(selectedDate.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [selectedDate]);

  const buildParams = () => {
    const params = new URLSearchParams();
    params.append("date", dateStr);
    if (plateFilter) params.append("plate_contains", plateFilter);
    const cams = (camerasText || "").split(/[\n,; ]+/).map(s => s.trim()).filter(Boolean);
    cams.forEach(c => params.append("camera_id", c));
    return params;
  };

  const loadDelta = async () => {
    try {
      setLoading(true);
      const params = buildParams();
      params.append("hours_threshold", String(threshold));
      params.append("gap_minutes", String(gap));
      params.append("include_open", "true");
      const res = await axios.get(`${API}/lpr/delta?${params.toString()}`, { headers: session.authHeaders() });
      setPairs(res.data || []);
      setActiveTab('analyse');
    } catch (e) {
      alert("Delta Abruf fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const params = buildParams();
      params.append("hours_threshold", String(threshold));
      params.append("gap_minutes", String(gap));
      params.append("include_open", "true");
      const res = await axios.get(`${API}/lpr/delta.csv?${params.toString()}`, { headers: session.authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `delta_pairs_${dateStr}_${threshold}h_gap${gap}m.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
    }
  };

  const loadSightings = async () => {
    try {
      setLoading(true);
      const params = buildParams();
      const res = await axios.get(`${API}/lpr/sightings?${params.toString()}`, { headers: session.authHeaders() });
      setRowsSight(res.data || []);
      setActiveTab('analyse');
      
      // Automatically load sightings statistics after raw sightings are loaded
      if (res.data && res.data.length > 0) {
        setTimeout(() => {
          loadDurationStatistics();
        }, 500); // Small delay to ensure UI updates
      }
    } catch (e) {
      alert("Rohsichtungen Abruf fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const downloadSightingsCsv = async () => {
    try {
      const params = buildParams();
      const res = await axios.get(`${API}/lpr/sightings.csv?${params.toString()}`, { headers: session.authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sightings_${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
    }
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: session.authHeaders() });
      setUserList(res.data || []);
    } catch (e) { }
  };
  const createUser = async () => {
    try {
      if (!newUser.email || !newUser.password) return alert("E-Mail & Passwort erforderlich");
      const res = await axios.post(`${API}/users`, newUser, { headers: session.authHeaders() });
      alert("Nutzer angelegt: " + res.data.email);
      setNewUser({ email: "", password: "", role: "user", support_enabled: false });
      await loadUsers();
    } catch (e) {
      alert("Nutzeranlage fehlgeschlagen");
    }
  };
  const deleteUser = async (emailParam) => {
    const target = emailParam || deleteEmail;
    if (!target) return alert("E-Mail zum Löschen eingeben");
    if (!window.confirm(t('confirm_delete_user'))) return;
    try {
      const res = await axios.delete(`${API}/users`, { data: { email: target }, headers: session.authHeaders() });
      if (res.data?.deleted) {
        alert("Nutzer gelöscht: " + target);
        setDeleteEmail("");
        await loadUsers();
      } else {
        alert("Kein Nutzer gelöscht (evtl. nicht gefunden)");
      }
    } catch (e) {
      alert("Löschen fehlgeschlagen");
    }
  };
  const resetPassword = async (emailParam) => {
    const npw = window.prompt(`Neues Passwort für ${emailParam}:`);
    if (!npw) return;
    try {
      const res = await axios.patch(`${API}/users`, { email: emailParam, password: npw }, { headers: session.authHeaders() });
      if (res.data?.updated) {
        alert("Passwort aktualisiert.");
      } else {
        alert("Keine Änderung.");
      }
    } catch (e) {
      alert("Update fehlgeschlagen");
    }
  };

  // Convert URLs in text to clickable links
  const renderTextWithLinks = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // URL regex pattern (improved to handle more URL formats)
    const urlPattern = /(https?:\/\/[^\s<>"{}|\\^`[\]\n\r]+)/gi;
    const urls = text.match(urlPattern);
    
    if (!urls) return text;
    
    // Split text and create clickable links
    const parts = text.split(urlPattern);
    return parts.map((part, index) => {
      if (urls.includes(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#2563eb',
              textDecoration: 'underline',
              cursor: 'pointer',
              wordBreak: 'break-all'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
              e.currentTarget.style.opacity = '0.8';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  // Render JSON with clickable links
  const renderJsonWithLinks = (jsonObj) => {
    const jsonString = JSON.stringify(jsonObj, null, 2);
    return renderTextWithLinks(jsonString);
  };

  // Format timestamp according to user's timezone settings with Verkada webhook support
  const formatTimestampWithTimezone = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const timezone = me.timezone || 'Europe/Berlin'; // Default fallback
      
      return date.toLocaleString('de-DE', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      // Fallback to simple format
      return new Date(timestamp).toLocaleString('de-DE');
    }
  };

  const navigateToModule = (module, subTab = null) => {
    if (module === 'analyse') {
      setActiveTab('analyse');
      if (subTab) setAnalysisSubTab(subTab);
    } else {
      setActiveTab(module);
    }
  };

  const filteredPairs = useMemo(() => {
    if (!plateFilter) return pairs;
    const needle = plateFilter.toUpperCase();
    return (pairs || []).filter(p => (p.license_plate || '').toUpperCase().includes(needle));
  }, [pairs, plateFilter]);

  const filteredSightings = useMemo(() => {
    if (!plateFilter) return rowsSight;
    const needle = plateFilter.toUpperCase();
    return (rowsSight || []).filter(p => (p.license_plate || '').toUpperCase().includes(needle));
  }, [rowsSight, plateFilter]);

  const filteredUsers = useMemo(() => {
    if (!userSearch) return userList;
    const n = userSearch.toLowerCase();
    return (userList || []).filter(u => (u.email || '').toLowerCase().includes(n));
  }, [userList, userSearch]);

  const rowBadgeColor = (h) => {
    if (h >= threshold + 6) return { background: 'rgba(239,68,68,0.08)', color: '#7f1d1d' };
    if (h >= threshold + 2) return { background: 'rgba(251,191,36,0.08)', color: '#78350f' };
    return { background: 'rgba(16,185,129,0.08)', color: '#064e3b' };
  };

  return (
    <div className="container light">
      <header className="topbar lightbar">
        <div className="brand darktxt">Verkada Analytics</div>
        <div className="tenant">{session.userToken ? (
          <>{t('app_user_label')} <b>{session.userEmail}</b> ({userRole || 'user'})</>
        ) : (
          <>{t('app_please_login')}</>
        )} · Region: {me.region?.toUpperCase()} · TZ: {me.timezone}</div>
        <div style={{display:'flex', gap:8, alignItems:'center'}}>
          <select value={lang} onChange={(e)=>setLang(e.target.value)} title="Sprache / Language">
            <option value="de">DE</option>
            <option value="en">EN</option>
          </select>
          <Button variant="secondary" onClick={session.clear}>Logout</Button>
        </div>
      </header>

      {/* Tabs */}
      {!isAdmin && (
        <div className="tabs" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <div style={{display: 'flex', gap: '0'}}>
            <div className={`tab ${activeTab==='dashboard'?'active':''}`} onClick={()=>setActiveTab('dashboard')}>🏠 {t('tabs_dashboard')}</div>
            
            {/* Analysis Dropdown Tab */}
            <div className={`tab ${activeTab==='analyse'?'active':''} dropdown-tab`} style={{position: 'relative'}}>
              <div 
                onClick={() => {
                  setActiveTab('analyse');
                  setShowAnalysisDropdown(!showAnalysisDropdown);
                }}
                style={{display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer'}}
              >
                Menu
                <span style={{fontSize: '12px', transition: 'transform 0.2s', transform: showAnalysisDropdown ? 'rotate(180deg)' : 'rotate(0deg)'}}>▼</span>
              </div>
            
            {/* Dropdown Menu */}
            {showAnalysisDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                boxShadow: '0 4px 12px -1px rgba(0, 0, 0, 0.15)',
                zIndex: 1000,
                minWidth: '260px',
                padding: '6px 0'
              }}>
                <div 
                  onClick={(e) => {
                    setAnalysisSubTab('analyse');
                    setShowAnalysisDropdown(false);
                  }}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    backgroundColor: analysisSubTab === 'analyse' ? '#f3f4f6' : 'transparent',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.target.style.backgroundColor = analysisSubTab === 'analyse' ? '#f3f4f6' : 'transparent'}
                >
                  📊 {t('tabs_analyse_sub')}
                </div>
                <div style={{height: '1px', backgroundColor: '#e5e7eb', margin: '4px 12px'}}></div>
                <div 
                  onClick={(e) => {
                    setAnalysisSubTab('status');
                    setShowAnalysisDropdown(false);
                  }}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    backgroundColor: analysisSubTab === 'status' ? '#f3f4f6' : 'transparent',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.target.style.backgroundColor = analysisSubTab === 'status' ? '#f3f4f6' : 'transparent'}
                >
                  📈 {t('tabs_status')}
                </div>
                <div style={{height: '1px', backgroundColor: '#e5e7eb', margin: '4px 12px'}}></div>
                <div 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAnalysisSubTab('audit');
                    setShowAnalysisDropdown(false);
                  }}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    backgroundColor: analysisSubTab === 'audit' ? '#f3f4f6' : 'transparent',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.target.style.backgroundColor = analysisSubTab === 'audit' ? '#f3f4f6' : 'transparent'}
                >
                  🔍 {t('tabs_audit')}
                </div>
                <div style={{height: '1px', backgroundColor: '#e5e7eb', margin: '4px 12px'}}></div>
                <div 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAnalysisSubTab('webhook-events');
                    setShowAnalysisDropdown(false);
                    loadWebhookEvents();
                  }}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    backgroundColor: analysisSubTab === 'webhook-events' ? '#f3f4f6' : 'transparent',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.target.style.backgroundColor = analysisSubTab === 'webhook-events' ? '#f3f4f6' : 'transparent'}
                >
                  🔗 {t('menu_webhooks')}
                </div>
                <div style={{height: '1px', backgroundColor: '#e5e7eb', margin: '4px 12px'}}></div>
                <div 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAnalysisSubTab('shipping');
                    setShowAnalysisDropdown(false);
                  }}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    backgroundColor: analysisSubTab === 'shipping' ? '#f3f4f6' : 'transparent',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.target.style.backgroundColor = analysisSubTab === 'shipping' ? '#f3f4f6' : 'transparent'}
                >
                  📦 {t('menu_shipping')}
                </div>
                <div style={{height: '1px', backgroundColor: '#e5e7eb', margin: '4px 12px'}}></div>
                <div 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAnalysisSubTab('kasse');
                    setShowAnalysisDropdown(false);
                  }}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    backgroundColor: analysisSubTab === 'kasse' ? '#f3f4f6' : 'transparent',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.target.style.backgroundColor = analysisSubTab === 'kasse' ? '#f3f4f6' : 'transparent'}
                >
                  💰 {t('menu_cash_register')}
                </div>
                {/* Automation Menu Item */}
                <div style={{height: '1px', backgroundColor: '#e5e7eb', margin: '4px 12px'}}></div>
                <div 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAnalysisSubTab('automation');
                    setShowAnalysisDropdown(false);
                  }}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    backgroundColor: analysisSubTab === 'automation' ? '#f3f4f6' : 'transparent',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.target.style.backgroundColor = analysisSubTab === 'automation' ? '#f3f4f6' : 'transparent'}
                  data-testid="menu-automation"
                >
                  ⚡ {t('menu_automation')}
                </div>
                {/* Live View Menu Item */}
                <div style={{height: '1px', backgroundColor: '#e5e7eb', margin: '4px 12px'}}></div>
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveTab('analyse');
                    setAnalysisSubTab('live-view');
                    setShowAnalysisDropdown(false);
                  }}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    backgroundColor: analysisSubTab === 'live-view' ? '#f3f4f6' : 'transparent',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.target.style.backgroundColor = analysisSubTab === 'live-view' ? '#f3f4f6' : 'transparent'}
                  data-testid="menu-live-view"
                >
                  📺 {t('menu_live_view')}
                </div>
                {/* Support Menu Item - only show if supportEnabled */}
                {supportEnabled && (
                <>
                <div style={{height: '1px', backgroundColor: '#e5e7eb', margin: '4px 12px'}}></div>
                <div 
                  onClick={() => {
                    setActiveTab('analyse');
                    setAnalysisSubTab('support');
                    setShowAnalysisDropdown(false);
                  }}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    backgroundColor: analysisSubTab === 'support' ? '#f3f4f6' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.target.style.backgroundColor = analysisSubTab === 'support' ? '#f3f4f6' : 'transparent'}
                >
                  🎧 {t('dashboard_module_support_title')}
                </div>
                </>
                )}
                {/* AI Analytics Menu Item */}
                {supportEnabled && (
                <>
                <div style={{height: '1px', backgroundColor: '#e5e7eb', margin: '4px 12px'}}></div>
                <div 
                  onClick={() => {
                    setActiveTab('analyse');
                    setAnalysisSubTab('ai-analytics');
                    setShowAnalysisDropdown(false);
                  }}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    backgroundColor: analysisSubTab === 'ai-analytics' ? '#f3f4f6' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.target.style.backgroundColor = analysisSubTab === 'ai-analytics' ? '#f3f4f6' : 'transparent'}
                >
                  <BrainCircuit size={16} /> {t('dashboard_module_ai_analytics_title')}
                </div>
                </>
                )}
              </div>
            )}
          </div>
          
          <div className={`tab ${activeTab==='settings'?'active':''}`} onClick={()=>setActiveTab('settings')}>{t('tabs_settings')}</div>
          <div className={`tab ${activeTab==='help'?'active':''}`} onClick={()=>setActiveTab('help')}>{t('tabs_help')}</div>
          </div>
          
          {/* Alarm Bell Icon */}
          <div style={{
            marginRight: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: alarmState === 'armed' ? '#dc2626' : '#6b7280'
            }}>
              {alarmState === 'armed' ? t('alarm_label') : t('alarm_off_label')}
            </span>
            {alarmState === 'armed' ? (
              <div style={{
                fontSize: '24px',
                color: '#dc2626',
                cursor: 'default'
              }} title={t('alarm_armed')}>
                🔔
              </div>
            ) : (
              <div style={{
                fontSize: '24px',
                color: '#9ca3af',
                cursor: 'default',
                position: 'relative',
                border: '2px solid #dc2626',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }} title={t('alarm_disarmed')}>
                🔕
              </div>
            )}
          </div>
        </div>
      )}
      {isAdmin && (
        <div className="tabs">
          <div className={`tab ${activeTab==='admin'?'active':''}`} onClick={()=>setActiveTab('admin')}>{t('admin_console')}</div>
          <div className={`tab ${activeTab==='help'?'active':''}`} onClick={()=>setActiveTab('help')}>{t('tabs_help')}</div>
        </div>
      )}

      {/* Dashboard Tab (Phase 5) */}
      {!isAdmin && activeTab === 'dashboard' && (
        <DashboardModule
          t={t}
          me={me}
          session={session}
          isMobile={isMobile}
          isTablet={isTablet}
          supportEnabled={supportEnabled}
          navigateToModule={navigateToModule}
        />
      )}


      {/* Analyse Sub-Tab */}
      {!isAdmin && activeTab === 'analyse' && analysisSubTab === 'analyse' && (
        <>
          <div style={{display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '380px 1fr', gap: '20px', marginBottom: '20px'}}>
            {/* Date Picker Card - Enhanced */}
            <Card className="panel lightCard" style={{border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
              <CardContent style={{padding: '20px'}}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                  paddingBottom: '12px',
                  borderBottom: '2px solid #0ea5e9'
                }}>
                  <span style={{fontSize: '24px'}}>📅</span>
                  <div>
                    <div className="panel-title darktxt" style={{marginBottom: '2px', fontSize: '16px'}}>
                      {t('date_pick')}
                    </div>
                    <div style={{fontSize: '11px', color: '#6b7280'}}>
                      {t('date_pick_hint')}
                    </div>
                  </div>
                </div>
                <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} className="calendar" />
                
                {/* Selected Date Display */}
                {selectedDate && (
                  <div style={{
                    marginTop: '12px',
                    padding: '10px',
                    backgroundColor: '#eff6ff',
                    borderRadius: '6px',
                    border: '1px solid #bfdbfe',
                    textAlign: 'center'
                  }}>
                    <div style={{fontSize: '11px', color: '#1e40af', marginBottom: '2px'}}>{t('date_selected')}</div>
                    <div style={{fontSize: '13px', fontWeight: '600', color: '#1e3a8a'}}>
                      {selectedDate.toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Configuration Card - Enhanced */}
            <Card className="panel lightCard" style={{border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
              <CardContent style={{padding: '20px'}}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '20px',
                  paddingBottom: '12px',
                  borderBottom: '2px solid #10b981'
                }}>
                  <span style={{fontSize: '24px'}}>⚙️</span>
                  <div>
                    <div className="panel-title darktxt" style={{marginBottom: '2px', fontSize: '16px'}}>
                      {t('configuration')}
                    </div>
                    <div style={{fontSize: '11px', color: '#6b7280'}}>
                      {t('configuration_hint')}
                    </div>
                  </div>
                </div>
                
                {/* Sliders in Grid */}
                <div style={{display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', marginBottom: '20px'}}>
                  {/* Threshold Slider */}
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '8px',
                    border: '1px solid #fbbf24'
                  }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                      <span style={{fontSize: '13px', fontWeight: '600', color: '#92400e'}}>⏱️ {t('threshold')}</span>
                      <span style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#f59e0b',
                        padding: '4px 12px',
                        backgroundColor: '#fff',
                        borderRadius: '6px',
                        border: '2px solid #fbbf24'
                      }}>
                        {threshold} h
                      </span>
                    </div>
                    <Slider value={[threshold]} min={0} max={48} step={1} onValueChange={(v) => setThreshold(v[0])} />
                  </div>

                  {/* Gap Slider */}
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#dbeafe',
                    borderRadius: '8px',
                    border: '1px solid #60a5fa'
                  }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                      <span style={{fontSize: '13px', fontWeight: '600', color: '#1e40af'}}>⏲️ {t('gap')}</span>
                      <span style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#3b82f6',
                        padding: '4px 12px',
                        backgroundColor: '#fff',
                        borderRadius: '6px',
                        border: '2px solid #60a5fa'
                      }}>
                        {gap} m
                      </span>
                    </div>
                    <Slider value={[gap]} min={0} max={30} step={1} onValueChange={(v) => setGap(v[0])} />
                  </div>
                </div>

                {/* Input Fields */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '16px',
                  marginBottom: '20px'
                }}>
                  {/* Camera IDs */}
                  <div style={{
                    padding: '14px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db'
                  }}>
                    <Label style={{
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      <span style={{fontSize: '16px'}}>📹</span>
                      {t('camera_ids')}
                    </Label>
                    <Input 
                      value={camerasText} 
                      onChange={(e) => setCamerasText(e.target.value)} 
                      placeholder="01dccf..., weitere…"
                      style={{
                        fontSize: '13px',
                        backgroundColor: '#fff',
                        border: '1px solid #d1d5db'
                      }}
                    />
                  </div>

                  {/* Plate Filter */}
                  <div style={{
                    padding: '14px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db'
                  }}>
                    <Label style={{
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      <span style={{fontSize: '16px'}}>🔍</span>
                      {t('plate_filter')}
                    </Label>
                    <Input 
                      value={plateFilter} 
                      onChange={(e) => setPlateFilter(e.target.value)} 
                      placeholder={t('plate_placeholder')}
                      style={{
                        fontSize: '13px',
                        backgroundColor: '#fff',
                        border: '1px solid #d1d5db'
                      }}
                    />
                  </div>
                </div>

                {/* Action Buttons - Enhanced */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '12px'
                }}>
                  <Button 
                    onClick={loadDelta} 
                    disabled={loading}
                    style={{
                      backgroundColor: loading ? '#9ca3af' : '#0ea5e9',
                      color: '#fff',
                      fontWeight: '600',
                      padding: '12px 20px',
                      fontSize: '14px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      border: 'none'
                    }}
                  >
                    {loading ? "⏳ Berechne…" : "📊 " + t('delta_calc')}
                  </Button>
                  {rowsSight.length > 0 && !loadingDurationStats && !durationStats && (
                    <Button 
                      onClick={loadDurationStatistics} 
                      disabled={loadingDurationStats} 
                      style={{
                        backgroundColor: '#fff',
                        color: '#0ea5e9',
                        fontWeight: '600',
                        padding: '12px 20px',
                        fontSize: '14px',
                        border: '2px solid #0ea5e9',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      📈 {t('load_statistics')}
                    </Button>
                  )}
                  <Button 
                    onClick={downloadCsv} 
                    disabled={!filteredPairs.length}
                    style={{
                      backgroundColor: !filteredPairs.length ? '#e5e7eb' : '#10b981',
                      color: !filteredPairs.length ? '#9ca3af' : '#fff',
                      fontWeight: '600',
                      padding: '12px 20px',
                      fontSize: '14px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      border: 'none'
                    }}
                  >
                    💾 {t('csv_download')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Results Table Card - Enhanced */}
          <Card className="panel lightCard" style={{
            marginBottom: '20px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <CardContent style={{padding: '20px'}}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '2px solid #8b5cf6'
              }}>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <span style={{fontSize: '24px'}}>📋</span>
                  <div>
                    <div className="panel-title darktxt" style={{fontSize: '16px'}}>
                      {t('delta_pairs')}
                    </div>
                    {filteredPairs.length > 0 && (
                      <div style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        marginTop: '2px'
                      }}>
                        {filteredPairs.length} {t('entries')} • {filteredPairs.filter(p => p.is_vip).length} {t('vip_plates')}
                      </div>
                    )}
                  </div>
                </div>
                {filteredPairs.length > 0 && (
                  <div style={{
                    padding: '8px 16px',
                    backgroundColor: '#eff6ff',
                    borderRadius: '8px',
                    border: '1px solid #3b82f6'
                  }}>
                    <span style={{fontSize: '20px', fontWeight: 'bold', color: '#1e40af'}}>
                      {filteredPairs.length}
                    </span>
                  </div>
                )}
              </div>

              {/* Color Legend - Enhanced */}
              {filteredPairs.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
                  gap: '12px',
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: '#fafafa',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    border: '2px solid #10b981'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: '#10b981',
                      borderRadius: '4px'
                    }}></div>
                    <span style={{fontSize: '12px', fontWeight: '500'}}>Unter Schwelle</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    border: '2px solid #f59e0b'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: '#f59e0b',
                      borderRadius: '4px'
                    }}></div>
                    <span style={{fontSize: '12px', fontWeight: '500'}}>Nahe Schwelle</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    border: '2px solid #ef4444'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: '#ef4444',
                      borderRadius: '4px'
                    }}></div>
                    <span style={{fontSize: '12px', fontWeight: '500'}}>Über Schwelle</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    backgroundColor: '#fef2f2',
                    borderRadius: '6px',
                    border: '2px solid #dc2626'
                  }}>
                    <span style={{fontSize: '18px'}}>🔴</span>
                    <span style={{fontSize: '12px', fontWeight: '600', color: '#dc2626'}}>{t('vip_plates')}</span>
                  </div>
                </div>
              )}

              {/* Table */}
              <div style={{overflowX: 'auto'}}>
                <Table>
                  <TableHeader>
                    <TableRow style={{backgroundColor: '#f9fafb'}}>
                      <TableHead style={{fontWeight: '600', color: '#374151'}}>{t('th_plate')}</TableHead>
                      <TableHead style={{fontWeight: '600', color: '#374151'}}>{t('th_entry')}</TableHead>
                      <TableHead style={{fontWeight: '600', color: '#374151'}}>{t('th_exit')}</TableHead>
                      <TableHead style={{fontWeight: '600', color: '#374151'}}>{t('th_dur_min')}</TableHead>
                      <TableHead style={{fontWeight: '600', color: '#374151'}}>{t('th_dur_h')}</TableHead>
                      <TableHead style={{fontWeight: '600', color: '#374151'}}>{t('th_cam_entry')}</TableHead>
                      <TableHead style={{fontWeight: '600', color: '#374151'}}>{t('th_cam_exit')}</TableHead>
                      <TableHead style={{fontWeight: '600', color: '#374151'}}>{t('th_image')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPairs.map((r, idx) => {
                      const baseStyle = rowBadgeColor(r.duration_hours);
                      const vipStyle = r.is_vip ? {
                        ...baseStyle,
                        backgroundColor: 'rgba(239,68,68,0.15)',
                        borderLeft: '4px solid #dc2626'
                      } : baseStyle;
                      
                      return (
                        <TableRow key={idx} style={vipStyle}>
                          <TableCell className="mono darktxt" style={r.is_vip ? {color: '#dc2626', fontWeight: 'bold'} : {}}>
                            {r.is_vip && <span style={{marginRight: 4}}>🔴</span>}
                            {r.license_plate}
                          </TableCell>
                          <TableCell className="mono small">{r.entry_time}</TableCell>
                          <TableCell className="mono small">{r.exit_time || '—'}</TableCell>
                          <TableCell className="darktxt">{r.duration_minutes}</TableCell>
                          <TableCell className="darktxt">{r.duration_hours}</TableCell>
                          <TableCell className="small">{r.entry_camera_id || ''}</TableCell>
                          <TableCell className="small">{r.exit_camera_id || ''}</TableCell>
                          <TableCell>
                            {r.entry_image_url ? (
                              <img src={r.entry_image_url} alt="entry" className="thumb" onClick={() => setPreview({ open: true, entry: r.entry_image_url, exit: r.exit_image_url || '', plate: r.license_plate })} style={{cursor: 'pointer', borderRadius: '4px'}} />
                            ) : null}
                            {r.exit_image_url ? (
                              <img src={r.exit_image_url} alt="exit" className="thumb ml8" onClick={() => setPreview({ open: true, entry: r.entry_image_url || '', exit: r.exit_image_url, plate: r.license_plate })} style={{cursor: 'pointer', borderRadius: '4px'}} />
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!filteredPairs.length && (
                      <TableRow>
                        <TableCell colSpan={8} style={{
                          textAlign: 'center',
                          padding: '60px 20px',
                          color: '#9ca3af'
                        }}>
                          {loading ? (
                            <div>
                              <div style={{fontSize: '32px', marginBottom: '12px'}}>⏳</div>
                              <div style={{fontSize: '14px', fontWeight: '500'}}>{t('data_loading')}</div>
                            </div>
                          ) : (
                            <div>
                              <div style={{fontSize: '32px', marginBottom: '12px'}}>📊</div>
                              <div style={{fontSize: '14px', fontWeight: '500'}}>{t('empty_delta')}</div>
                              <div style={{fontSize: '12px', marginTop: '6px'}}>{t('click_delta_calculate')}</div>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Duration Statistics Chart - Enhanced */}
          {durationStats && rowsSight.length > 0 && (
            <Card className="panel lightCard" style={{
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <CardContent style={{padding: '20px'}}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '16px',
                  paddingBottom: '12px',
                  borderBottom: '2px solid #6366f1'
                }}>
                  <span style={{fontSize: '24px'}}>📊</span>
                  <div style={{flex: 1}}>
                    <div className="panel-title darktxt" style={{fontSize: '16px'}}>
                      Sichtungen pro Stunde
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      marginTop: '2px'
                    }}>
                      {formatDisplayDate(durationStats.date)}
                    </div>
                  </div>
                  <div style={{
                    padding: '10px 20px',
                    backgroundColor: '#eef2ff',
                    borderRadius: '8px',
                    border: '1px solid #818cf8',
                    textAlign: 'center'
                  }}>
                    <div style={{fontSize: '10px', color: '#4338ca', fontWeight: '500'}}>Total</div>
                    <div style={{fontSize: '20px', fontWeight: 'bold', color: '#4f46e5'}}>
                      {durationStats.total_vehicles}
                    </div>
                  </div>
                </div>
                <div style={{
                  height: '340px',
                  position: 'relative',
                  padding: '20px',
                  backgroundColor: '#fafafa',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb'
                }}>
                  <Bar 
                    data={{
                      labels: durationStats.hourly_stats.map(stat => `${stat.hour.toString().padStart(2, '0')}:00`),
                      datasets: [{
                        label: 'Anzahl Sichtungen',
                        data: durationStats.hourly_stats.map(stat => {
                          return stat.vehicle_count;
                        }),
                        backgroundColor: 'rgba(59, 130, 246, 0.6)',
                        borderColor: 'rgba(59, 130, 246, 1)',
                        borderWidth: 1,
                        borderRadius: 4,
                        borderSkipped: false,
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: false
                        },
                        legend: {
                          display: false
                        },
                        tooltip: {
                          callbacks: {
                            title: (tooltipItems) => {
                              const hour = parseInt(tooltipItems[0].label.split(':')[0]);
                              return `${hour.toString().padStart(2, '0')}:00 - ${(hour + 1).toString().padStart(2, '0')}:00`;
                            },
                            label: (context) => {
                              const stat = durationStats.hourly_stats[context.dataIndex];
                              return `Sichtungen: ${stat.vehicle_count}`;
                            }
                          }
                        }
                      },
                      scales: {
                        x: {
                          title: {
                            display: true,
                            text: 'Tageszeit'
                          },
                          grid: {
                            display: false
                          }
                        },
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Anzahl Sichtungen'
                          },
                          ticks: {
                            stepSize: 1,
                            callback: function(value) {
                              return Number.isInteger(value) ? value : '';
                            }
                          },
                          suggestedMax: Math.max(...(durationStats.hourly_stats?.map(s => s.vehicle_count) || [0])) + 1
                        }
                      },
                      animation: {
                        duration: 1000
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Raw Sightings Table - Enhanced */}
          <Card className="panel lightCard" style={{
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <CardContent style={{padding: '20px'}}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '2px solid #64748b'
              }}>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <span style={{fontSize: '24px'}}>🚗</span>
                  <div>
                    <div className="panel-title darktxt" style={{fontSize: '16px'}}>
                      {t('sightings')}
                    </div>
                    {filteredSightings.length > 0 && (
                      <div style={{fontSize: '11px', color: '#6b7280', marginTop: '2px'}}>
                        {filteredSightings.length} Sichtungen • {filteredSightings.filter(s => s.is_vip).length} VIP
                      </div>
                    )}
                  </div>
                </div>
                {filteredSightings.length > 0 && (
                  <div style={{
                    padding: '8px 16px',
                    backgroundColor: '#f1f5f9',
                    borderRadius: '8px',
                    border: '1px solid #64748b'
                  }}>
                    <span style={{fontSize: '20px', fontWeight: 'bold', color: '#334155'}}>
                      {filteredSightings.length}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
                flexWrap: 'wrap'
              }}>
                <Button 
                  onClick={loadSightings} 
                  disabled={loading}
                  style={{
                    backgroundColor: loading ? '#9ca3af' : '#64748b',
                    color: '#fff',
                    fontWeight: '600',
                    padding: '10px 18px',
                    fontSize: '13px',
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  {loading ? '⏳ ' + t('loading') : '📥 ' + t('load_sightings')}
                </Button>
                <Button 
                  onClick={downloadSightingsCsv} 
                  disabled={!filteredSightings.length}
                  style={{
                    backgroundColor: !filteredSightings.length ? '#e5e7eb' : '#10b981',
                    color: !filteredSightings.length ? '#9ca3af' : '#fff',
                    fontWeight: '600',
                    padding: '10px 18px',
                    fontSize: '13px',
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  💾 {t('sightings_csv')}
                </Button>
              </div>

              {/* VIP Legend */}
              {filteredSightings.filter(s => s.is_vip).length > 0 && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '8px',
                  border: '2px solid #dc2626',
                  marginBottom: '16px'
                }}>
                  <span style={{fontSize: '16px'}}>🔴</span>
                  <span style={{fontSize: '12px', fontWeight: '600', color: '#dc2626'}}>
                    {t('vip_plates_highlighted')}
                  </span>
                </div>
              )}

              {/* Table */}
              <div style={{overflowX: 'auto'}}>
                <Table>
                  <TableHeader>
                    <TableRow style={{backgroundColor: '#f8fafc'}}>
                      <TableHead style={{fontWeight: '600', color: '#374151', width: '30%'}}>
                        {t('th_plate')}
                      </TableHead>
                      <TableHead style={{fontWeight: '600', color: '#374151', width: '35%'}}>
                        📅 Timestamp
                      </TableHead>
                      <TableHead style={{fontWeight: '600', color: '#374151', width: '35%'}}>
                        📹 Kamera
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rowsSight.length ? filteredSightings.map((r, idx) => (
                      <TableRow 
                        key={idx} 
                        style={{
                          backgroundColor: r.is_vip ? 'rgba(239,68,68,0.1)' : (idx % 2 === 0 ? '#fff' : '#fafafa'),
                          borderLeft: r.is_vip ? '4px solid #dc2626' : 'none',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          if (!r.is_vip) {
                            e.currentTarget.style.backgroundColor = '#f1f5f9';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!r.is_vip) {
                            e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#fff' : '#fafafa';
                          }
                        }}
                      >
                        <TableCell 
                          className="mono" 
                          style={{
                            color: r.is_vip ? '#dc2626' : '#111827',
                            fontWeight: r.is_vip ? 'bold' : '600',
                            fontSize: '14px'
                          }}
                        >
                          {r.is_vip && <span style={{marginRight: 6, fontSize: '16px'}}>🔴</span>}
                          {r.license_plate}
                        </TableCell>
                        <TableCell 
                          className="mono small" 
                          style={{
                            fontSize: '13px',
                            color: '#4b5563'
                          }}
                        >
                          {r.timestamp}
                        </TableCell>
                        <TableCell 
                          className="small"
                          style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            fontFamily: 'monospace'
                          }}
                        >
                          {r.camera_id}
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={3} style={{
                          textAlign: 'center',
                          padding: '60px 20px',
                          color: '#9ca3af'
                        }}>
                          {loading ? (
                            <div>
                              <div style={{fontSize: '32px', marginBottom: '12px'}}>⏳</div>
                              <div style={{fontSize: '14px', fontWeight: '500'}}>{t('data_loading')}</div>
                            </div>
                          ) : (
                            <div>
                              <div style={{fontSize: '32px', marginBottom: '12px'}}>🚗</div>
                              <div style={{fontSize: '14px', fontWeight: '500'}}>{t('empty_sight')}</div>
                              <div style={{fontSize: '12px', marginTop: '6px'}}>{t('click_load_sightings')}</div>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Einstellungen Tab */}
      {!isAdmin && activeTab === 'settings' && (
        <SettingsModule
          t={t}
          lang={lang}
          session={session}
          me={me} setMe={setMe}
          xApiKey={xApiKey} setXApiKey={setXApiKey}
          hasXKey={hasXKey} setHasXKey={setHasXKey}
          camerasText={camerasText} setCamerasText={setCamerasText}
          savedCameras={savedCameras} setSavedCameras={setSavedCameras}
          positives={positives}
          positivesInput={positivesInput} setPositivesInput={setPositivesInput}
          plateFilter={plateFilter}
          threshold={threshold}
          gap={gap}
          vipPlates={vipPlates}
          vipInput={vipInput} setVipInput={setVipInput}
          vipDescription={vipDescription} setVipDescription={setVipDescription}
          syncingVips={syncingVips} setSyncingVips={setSyncingVips}
          kassenUID={kassenUID} setKassenUID={setKassenUID}
          kassenCameraID={kassenCameraID} setKassenCameraID={setKassenCameraID}
          savedKassenUID={savedKassenUID} setSavedKassenUID={setSavedKassenUID}
          savedKassenCameraID={savedKassenCameraID} setSavedKassenCameraID={setSavedKassenCameraID}
          shippingUID={shippingUID} setShippingUID={setShippingUID}
          shippingCameraID={shippingCameraID} setShippingCameraID={setShippingCameraID}
          savedShippingUID={savedShippingUID} setSavedShippingUID={setSavedShippingUID}
          savedShippingCameraID={savedShippingCameraID} setSavedShippingCameraID={setSavedShippingCameraID}
          shellyServer={shellyServer} setShellyServer={setShellyServer}
          shellyAuthKey={shellyAuthKey} setShellyAuthKey={setShellyAuthKey}
          shellyDevices={shellyDevices} setShellyDevices={setShellyDevices}
          savedShellyServer={savedShellyServer} setSavedShellyServer={setSavedShellyServer}
          savedShellyAuthKey={savedShellyAuthKey} setSavedShellyAuthKey={setSavedShellyAuthKey}
          savedShellyDevices={savedShellyDevices} setSavedShellyDevices={setSavedShellyDevices}
          liveViews={liveViews} setLiveViews={setLiveViews}
          savedLiveViews={savedLiveViews} setSavedLiveViews={setSavedLiveViews}
          aiAnalyticsCameraId={aiAnalyticsCameraId} setAiAnalyticsCameraId={setAiAnalyticsCameraId}
          aiAnalyticsGeminiKey={aiAnalyticsGeminiKey} setAiAnalyticsGeminiKey={setAiAnalyticsGeminiKey}
          aiAnalyticsGeminiKeySaved={aiAnalyticsGeminiKeySaved} setAiAnalyticsGeminiKeySaved={setAiAnalyticsGeminiKeySaved}
          aiAnalyticsHelixUid={aiAnalyticsHelixUid} setAiAnalyticsHelixUid={setAiAnalyticsHelixUid}
          aiAnalyticsOrgId={aiAnalyticsOrgId} setAiAnalyticsOrgId={setAiAnalyticsOrgId}
          webhookConfig={webhookConfig}
          webhookURL={webhookURL} setWebhookURL={setWebhookURL}
          webhookNotifications={webhookNotifications}
          loadingWebhook={loadingWebhook}
          newWebhookToken={newWebhookToken} setNewWebhookToken={setNewWebhookToken}
          creatingWebhook={creatingWebhook} setCreatingWebhook={setCreatingWebhook}
          savingPrefs={savingPrefs} setSavingPrefs={setSavingPrefs}
          refreshPositives={refreshPositives}
          refreshVipPlates={refreshVipPlates}
          loadWebhookConfig={loadWebhookConfig}
          loadWebhookEvents={loadWebhookEvents}
          formatTimestampWithTimezone={formatTimestampWithTimezone}
          setActiveTab={setActiveTab}
          setAnalysisSubTab={setAnalysisSubTab}
          twilioAccountSid={twilioAccountSid} setTwilioAccountSid={setTwilioAccountSid}
          twilioAuthToken={twilioAuthToken} setTwilioAuthToken={setTwilioAuthToken}
          twilioPhoneNumber={twilioPhoneNumber} setTwilioPhoneNumber={setTwilioPhoneNumber}
          savedTwilioAccountSid={savedTwilioAccountSid} setSavedTwilioAccountSid={setSavedTwilioAccountSid}
          savedTwilioAuthToken={savedTwilioAuthToken} setSavedTwilioAuthToken={setSavedTwilioAuthToken}
          savedTwilioPhoneNumber={savedTwilioPhoneNumber} setSavedTwilioPhoneNumber={setSavedTwilioPhoneNumber}
        />
      )}

      {/* Status Sub-Tab */}
      {!isAdmin && activeTab === 'analyse' && analysisSubTab === 'status' && (
        <StatusModule
          t={t}
          isMobile={isMobile}
          hasXKey={hasXKey}
          cameras={cameras}
          loadingCameras={loadingCameras}
          loadCameras={loadCameras}
        />
      )}

      {/* Webhook Events Sub-Tab */}
      {!isAdmin && activeTab === 'analyse' && analysisSubTab === 'webhook-events' && (
        <WebhooksModule
          t={t}
          webhookEvents={webhookEvents}
          webhookConfig={webhookConfig}
          webhookNotifications={webhookNotifications}
          loadWebhookEvents={loadWebhookEvents}
          getEventCategoryColor={getEventCategoryColor}
          getEventCategoryName={getEventCategoryName}
          getEventTypeEmoji={getEventTypeEmoji}
          formatEventType={formatEventType}
          formatTimestampWithTimezone={formatTimestampWithTimezone}
          renderJsonWithLinks={renderJsonWithLinks}
          renderTextWithLinks={renderTextWithLinks}
        />
      )}

      {/* Hilfe Tab */}
      {activeTab === 'help' && (
        <HelpModule t={t} />
      )}

      {/* Kasse Sub-Tab */}
      {!isAdmin && activeTab === 'analyse' && analysisSubTab === 'kasse' && (
        <KasseModule
          t={t}
          lang={lang}
          isMobile={isMobile}
          API={API}
          session={session}
          kasseItems={kasseItems}
          setKasseItems={setKasseItems}
          kasseTotalAmount={kasseTotalAmount}
          setKasseTotalAmount={setKasseTotalAmount}
          kasseCustomerName={kasseCustomerName}
          setKasseCustomerName={setKasseCustomerName}
          kasseNewItemName={kasseNewItemName}
          setKasseNewItemName={setKasseNewItemName}
          kasseNewItemPrice={kasseNewItemPrice}
          setKasseNewItemPrice={setKasseNewItemPrice}
          kasseNewItemQuantity={kasseNewItemQuantity}
          setKasseNewItemQuantity={setKasseNewItemQuantity}
          kasseEvents={kasseEvents}
          setKasseEvents={setKasseEvents}
          kasseSearch={kasseSearch}
          setKasseSearch={setKasseSearch}
          kasseSortBy={kasseSortBy}
          setKasseSortBy={setKasseSortBy}
          kasseSortOrder={kasseSortOrder}
          setKasseSortOrder={setKasseSortOrder}
          selectedKasseReceipt={selectedKasseReceipt}
          setSelectedKasseReceipt={setSelectedKasseReceipt}
          savedKassenCameraID={savedKassenCameraID}
          setSelectedThumbnail={setSelectedThumbnail}
          loadKasseEvents={loadKasseEvents}
        />
      )}

      {/* Admin View */}
      {isAdmin && activeTab === 'admin' && (
        <Card className="panel lightCard">
          <CardContent>
            <div className="panel-title darktxt">{t('admin_console')}</div>
            <div className="field twoCol">
              <div>
                <Label>{t('admin_new_user')}</Label>
                <Input value={newUser.email} onChange={(e) => setNewUser(u => ({...u, email: e.target.value}))} placeholder="user@example.com" />
              </div>
              <div>
                <Label>{t('admin_password')}</Label>
                <Input type="password" autoComplete="new-password" value={newUser.password} onChange={(e) => setNewUser(u => ({...u, password: e.target.value}))} placeholder="********" />
              </div>
            </div>
            <div className="field twoCol">
              <div>
                <Label>{t('admin_role')}</Label>
                <Input value={newUser.role} onChange={(e) => setNewUser(u => ({...u, role: e.target.value}))} placeholder="user | admin" />
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '24px'}}>
                <input 
                  type="checkbox" 
                  id="support_enabled_new"
                  checked={newUser.support_enabled} 
                  onChange={(e) => setNewUser(u => ({...u, support_enabled: e.target.checked}))}
                  style={{width: '18px', height: '18px', cursor: 'pointer'}}
                />
                <Label htmlFor="support_enabled_new" style={{cursor: 'pointer', marginBottom: 0}}>
                  🎧 {t('admin_support_enabled')}
                </Label>
              </div>
            </div>
            <div className="field" style={{marginTop: '8px'}}>
              <div className="actions alignEnd">
                <Button onClick={createUser}>{t('admin_create')}</Button>
                <Button variant="secondary" onClick={loadUsers}>{t('admin_refresh')}</Button>
              </div>
            </div>

            <div className="panel-title darktxt" style={{marginTop:12}}>{t('admin_users_title')}</div>
            <div className="field">
              <Label>{t('admin_search')}</Label>
              <Input value={userSearch} onChange={(e)=>setUserSearch(e.target.value)} placeholder="E-Mail filtern" />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Rolle</TableHead>
                  <TableHead>🎧 Support</TableHead>
                  <TableHead>Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((u, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="small">{u.email}</TableCell>
                    <TableCell className="small">{u.role}</TableCell>
                    <TableCell>
                      <input 
                        type="checkbox" 
                        checked={u.support_enabled || false}
                        onChange={async (e) => {
                          try {
                            await axios.patch(`${API}/users`, 
                              { email: u.email, support_enabled: e.target.checked },
                              { headers: session.authHeaders() }
                            );
                            loadUsers();
                          } catch (err) {
                            alert('Fehler beim Aktualisieren');
                          }
                        }}
                        style={{width: '18px', height: '18px', cursor: 'pointer'}}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="rowActions">
                        <Button variant="secondary" onClick={() => resetPassword(u.email)}>{t('admin_pw_reset')}</Button>
                        <Button variant="secondary" onClick={() => deleteUser(u.email)}>{t('admin_delete')}</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!filteredUsers.length && (
                  <TableRow>
                    <TableCell colSpan={4}>{t('app_no_users')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <hr className="divider" />
            <div className="field">
              <Label>{t('admin_delete_field')}</Label>
              <Input value={deleteEmail} onChange={(e) => setDeleteEmail(e.target.value)} placeholder="user@example.com" />
            </div>
            <div className="actions">
              <Button variant="secondary" onClick={() => deleteUser()}>{t('admin_delete_btn')}</Button>
            </div>
            <p className="hint">{t('hint_admin_no_lpr')}</p>
          </CardContent>
        </Card>
      )}

      {/* Shipping Sub-Tab */}
      {!isAdmin && activeTab === 'analyse' && analysisSubTab === 'shipping' && (
        <ShippingModule
          t={t}
          lang={lang}
          isMobile={isMobile}
          session={session}
          me={me}
          shippingAddress={shippingAddress}
          setShippingAddress={setShippingAddress}
          shippingContent={shippingContent}
          setShippingContent={setShippingContent}
          shippingEvents={shippingEvents}
          setShippingEvents={setShippingEvents}
          shippingSearch={shippingSearch}
          setShippingSearch={setShippingSearch}
          shippingSortBy={shippingSortBy}
          setShippingSortBy={setShippingSortBy}
          shippingSortOrder={shippingSortOrder}
          setShippingSortOrder={setShippingSortOrder}
          currentTrackingNumber={currentTrackingNumber}
          setCurrentTrackingNumber={setCurrentTrackingNumber}
          savedShippingUID={savedShippingUID}
          savedShippingCameraID={savedShippingCameraID}
          setSelectedThumbnail={setSelectedThumbnail}
          setSelectedEmailEvent={setSelectedEmailEvent}
          loadShippingEvents={loadShippingEvents}
        />
      )}

      {/* Support Sub-Tab */}
      {!isAdmin && activeTab === 'analyse' && analysisSubTab === 'support' && supportEnabled && (
        <SupportModule t={t} isMobile={isMobile} />
      )}

      {/* AI Analytics Sub-Tab */}
      {!isAdmin && activeTab === 'analyse' && analysisSubTab === 'ai-analytics' && supportEnabled && (
        <AiAnalyticsModule
          t={t}
          lang={lang}
          isMobile={isMobile}
          session={session}
          aiAnalyticsImage={aiAnalyticsImage}
          setAiAnalyticsImage={setAiAnalyticsImage}
          aiAnalyticsInstruction={aiAnalyticsInstruction}
          setAiAnalyticsInstruction={setAiAnalyticsInstruction}
          aiAnalyticsResult={aiAnalyticsResult}
          setAiAnalyticsResult={setAiAnalyticsResult}
          aiAnalyticsLoading={aiAnalyticsLoading}
          setAiAnalyticsLoading={setAiAnalyticsLoading}
          aiAnalyticsEpochTimestamp={aiAnalyticsEpochTimestamp}
          setAiAnalyticsEpochTimestamp={setAiAnalyticsEpochTimestamp}
          aiAnalyticsAnalyzing={aiAnalyticsAnalyzing}
          setAiAnalyticsAnalyzing={setAiAnalyticsAnalyzing}
          aiAnalyticsImageSource={aiAnalyticsImageSource}
          setAiAnalyticsImageSource={setAiAnalyticsImageSource}
          aiAnalyticsHelixStatus={aiAnalyticsHelixStatus}
          setAiAnalyticsHelixStatus={setAiAnalyticsHelixStatus}
          aiAnalyticsHistory={aiAnalyticsHistory}
          aiAnalyticsCameraId={aiAnalyticsCameraId}
          aiAnalyticsGeminiKeySaved={aiAnalyticsGeminiKeySaved}
          aiAnalyticsHelixUid={aiAnalyticsHelixUid}
          aiAnalyticsOrgId={aiAnalyticsOrgId}
          customPromptInput={customPromptInput}
          setCustomPromptInput={setCustomPromptInput}
          customPromptResult={customPromptResult}
          setCustomPromptResult={setCustomPromptResult}
          customPromptLoading={customPromptLoading}
          setCustomPromptLoading={setCustomPromptLoading}
          loadAiAnalyticsHistory={loadAiAnalyticsHistory}
          aiAutoRefreshEnabled={aiAutoRefreshEnabled}
          setAiAutoRefreshEnabled={setAiAutoRefreshEnabled}
          aiAutoRefreshMinutes={aiAutoRefreshMinutes}
          setAiAutoRefreshMinutes={setAiAutoRefreshMinutes}
          aiAutoRefreshPreset={aiAutoRefreshPreset}
          setAiAutoRefreshPreset={setAiAutoRefreshPreset}
        />
      )}

      {/* Automation Sub-Tab */}
      {!isAdmin && activeTab === 'analyse' && analysisSubTab === 'automation' && (
        <AutomationModule
          t={t}
          lang={lang}
          isMobile={isMobile}
          session={session}
          savedShellyServer={savedShellyServer}
          savedShellyAuthKey={savedShellyAuthKey}
          savedShellyDevices={savedShellyDevices}
          automationLprShellyEnabled={automationLprShellyEnabled}
          setAutomationLprShellyEnabled={setAutomationLprShellyEnabled}
          automationLprRejectedShellyEnabled={automationLprRejectedShellyEnabled}
          setAutomationLprRejectedShellyEnabled={setAutomationLprRejectedShellyEnabled}
          automationDisarmedCallEnabled={automationDisarmedCallEnabled}
          setAutomationDisarmedCallEnabled={setAutomationDisarmedCallEnabled}
          automationDisarmedCallTo={automationDisarmedCallTo}
          setAutomationDisarmedCallTo={setAutomationDisarmedCallTo}
          automationDisarmedCallLanguage={automationDisarmedCallLanguage}
          setAutomationDisarmedCallLanguage={setAutomationDisarmedCallLanguage}
          automationDisarmedCallText={automationDisarmedCallText}
          setAutomationDisarmedCallText={setAutomationDisarmedCallText}
          automationArmedCallEnabled={automationArmedCallEnabled}
          setAutomationArmedCallEnabled={setAutomationArmedCallEnabled}
          automationArmedCallTo={automationArmedCallTo}
          setAutomationArmedCallTo={setAutomationArmedCallTo}
          automationArmedCallLanguage={automationArmedCallLanguage}
          setAutomationArmedCallLanguage={setAutomationArmedCallLanguage}
          automationArmedCallText={automationArmedCallText}
          setAutomationArmedCallText={setAutomationArmedCallText}
          savedTwilioPhoneNumber={savedTwilioPhoneNumber}
          savedTwilioAccountSid={savedTwilioAccountSid}
          savedTwilioAuthToken={savedTwilioAuthToken}
        />
      )}

      {/* Live View Sub-Tab */}
      {!isAdmin && activeTab === 'analyse' && analysisSubTab === 'live-view' && (
        <LiveViewModule
          t={t}
          isMobile={isMobile}
          liveViews={liveViews}
          setActiveTab={setActiveTab}
        />
      )}

      {/* Audit Sub-Tab */}
      {!isAdmin && activeTab === 'analyse' && analysisSubTab === 'audit' && (
        <AuditModule
          t={t}
          lang={lang}
          hasXKey={hasXKey}
          auditTimeRange={auditTimeRange}
          setAuditTimeRange={setAuditTimeRange}
          auditCustomDays={auditCustomDays}
          setAuditCustomDays={setAuditCustomDays}
          auditPageSize={auditPageSize}
          setAuditPageSize={setAuditPageSize}
          auditLogs={auditLogs}
          loadingAudit={loadingAudit}
          auditNextPageToken={auditNextPageToken}
          auditSelectedItems={auditSelectedItems}
          auditSelectAll={auditSelectAll}
          auditSortField={auditSortField}
          auditSortDirection={auditSortDirection}
          loadAuditLogs={loadAuditLogs}
          sortAuditLogs={sortAuditLogs}
          handleSelectAll={handleSelectAll}
          handleSelectItem={handleSelectItem}
          exportSelected={exportSelected}
          clearSelection={clearSelection}
          formatTimestamp={formatTimestamp}
        />
      )}

      <Dialog open={preview.open} onOpenChange={(v) => setPreview(p => ({...p, open: v}))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bildvorschau – {preview.plate}</DialogTitle>
          </DialogHeader>
          <div className="previewRow">
            {preview.entry ? (
              <img src={preview.entry} alt="entry-large" className="previewImg" />
            ) : null}
            {preview.exit ? (
              <img src={preview.exit} alt="exit-large" className="previewImg" />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Thumbnail Modal */}
      <Dialog open={!!selectedThumbnail} onOpenChange={() => setSelectedThumbnail(null)}>
        <DialogContent style={{maxWidth: '90vw', maxHeight: '90vh', padding: '20px'}}>
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
            {selectedThumbnail && (
              <img 
                src={selectedThumbnail} 
                alt="Thumbnail vergrößert" 
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={!!selectedEmailEvent} onOpenChange={() => setSelectedEmailEvent(null)}>
        <DialogContent style={{maxWidth: '500px', padding: 0, overflow: 'hidden'}}>
          {selectedEmailEvent && (() => {
            // Extract customer name from address (first line)
            const addressLines = (selectedEmailEvent.address || '').split('\n');
            const customerName = addressLines[0] || 'Kunde';
            
            // Format date
            let formattedDate = '';
            if (selectedEmailEvent.timestamp) {
              const date = new Date(selectedEmailEvent.timestamp);
              formattedDate = date.toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', { 
                day: '2-digit', 
                month: 'long', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });
            }
            
            return (
              <div style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                backgroundColor: '#ffffff'
              }}>
                {/* Email Header */}
                <div style={{
                  backgroundColor: '#f8f9fa',
                  padding: '15px 20px',
                  borderBottom: '1px solid #dee2e6'
                }}>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '6px'
                  }}>
                    {t('email_subject')}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#6c757d'
                  }}>
                    <strong>{t('email_from')}:</strong> noreply@helix-shop.com<br/>
                    <strong>{t('email_to')}:</strong> {customerName.toLowerCase().replace(/\s+/g, '.')}@email.com<br/>
                    <strong>{t('email_date')}:</strong> {formattedDate}
                  </div>
                </div>

                {/* Email Body */}
                <div style={{
                  padding: '20px',
                  lineHeight: '1.5',
                  color: '#212529'
                }}>
                  <p style={{marginTop: 0, fontSize: '14px'}}>
                    {t('email_greeting')} <strong>{customerName}</strong>,
                  </p>
                  
                  <p style={{fontSize: '13px'}}>
                    {t('email_body_text')} <strong>{selectedEmailEvent.content}</strong> {t('email_body_tracking')} <span style={{
                      fontFamily: 'monospace',
                      backgroundColor: '#f8f9fa',
                      padding: '2px 4px',
                      borderRadius: '3px',
                      fontSize: '12px'
                    }}>{selectedEmailEvent.tracking_number}</span> {t('email_body_shipped')}
                  </p>

                  {/* Thumbnail */}
                  {selectedEmailEvent.thumbnail && (
                    <div style={{
                      margin: '15px 0',
                      textAlign: 'center',
                      backgroundColor: '#f8f9fa',
                      padding: '12px',
                      borderRadius: '6px'
                    }}>
                      <img 
                        src={selectedEmailEvent.thumbnail} 
                        alt="Versandaufnahme"
                        style={{
                          maxWidth: '250px',
                          height: 'auto',
                          borderRadius: '4px',
                          border: '1px solid #dee2e6',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                      />
                      <div style={{
                        fontSize: '11px',
                        color: '#6c757d',
                        marginTop: '8px'
                      }}>
                        {t('email_thumbnail_caption')}
                      </div>
                    </div>
                  )}

                  <p style={{fontSize: '13px'}}>
                    {t('email_closing')}
                  </p>
                </div>

                {/* Email Footer */}
                <div style={{
                  backgroundColor: '#f8f9fa',
                  padding: '15px 20px',
                  borderTop: '1px solid #dee2e6',
                  fontSize: '11px',
                  color: '#6c757d',
                  lineHeight: '1.4'
                }}>
                  <div style={{marginBottom: '8px'}}>
                    <strong>{t('email_footer_company')}</strong><br/>
                    {t('email_footer_address_line1')}<br/>
                    {t('email_footer_address_line2')}
                  </div>
                  <div style={{fontSize: '10px', color: '#adb5bd'}}>
                    {t('email_footer_disclaimer')}
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>


      {/* Receipt Modal */}
      <Dialog open={!!selectedKasseReceipt} onOpenChange={() => setSelectedKasseReceipt(null)}>
        <DialogContent style={{maxWidth: '320px', padding: 0, overflow: 'hidden'}}>
          {selectedKasseReceipt && (() => {
            // Format date and time
            let formattedDate = '';
            let formattedTime = '';
            let receiptNumber = '';
            
            if (selectedKasseReceipt.timestamp) {
              const date = new Date(selectedKasseReceipt.timestamp);
              formattedDate = date.toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric'
              });
              formattedTime = date.toLocaleTimeString(lang === 'de' ? 'de-DE' : 'en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                second: '2-digit'
              });
              // Generate receipt number from timestamp
              receiptNumber = `${date.getFullYear()}${String(date.getMonth()+1).padStart(2,'0')}${String(date.getDate()).padStart(2,'0')}-${selectedKasseReceipt.epoch_timestamp.toString().slice(-6)}`;
            }
            
            const isReturn = selectedKasseReceipt.flagged;
            const subtotal = selectedKasseReceipt.total_amount || 0;
            const taxAmount = subtotal * 0.19;
            const totalWithoutTax = subtotal / 1.19;
            
            return (
              <div style={{
                fontFamily: '"Courier New", monospace',
                backgroundColor: '#ffffff',
                fontSize: '11px'
              }}>
                {/* Receipt Header */}
                <div style={{
                  textAlign: 'center',
                  padding: '12px 16px',
                  borderBottom: '2px dashed #9ca3af',
                  backgroundColor: '#f9fafb'
                }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '2px'
                  }}>
                    {t('receipt_store_name')}
                  </div>
                  <div style={{
                    fontSize: '9px',
                    color: '#6b7280',
                    marginBottom: '8px'
                  }}>
                    {t('receipt_store_address')}
                  </div>
                  
                  {isReturn && (
                    <div style={{
                      backgroundColor: '#fef2f2',
                      color: '#dc2626',
                      padding: '4px 8px',
                      marginTop: '8px',
                      fontWeight: 'bold',
                      fontSize: '12px',
                      border: '2px solid #dc2626',
                      borderRadius: '3px'
                    }}>
                      ↩️ {t('receipt_return')}
                    </div>
                  )}
                </div>

                {/* Receipt Info */}
                <div style={{
                  padding: '10px 16px',
                  borderBottom: '1px dashed #d1d5db',
                  fontSize: '10px'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '2px'}}>
                    <span>{t('receipt_date')}:</span>
                    <span style={{fontWeight: 'bold'}}>{formattedDate}</span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '2px'}}>
                    <span>{t('receipt_time')}:</span>
                    <span style={{fontWeight: 'bold'}}>{formattedTime}</span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '2px'}}>
                    <span>{t('receipt_receipt_no')}:</span>
                    <span style={{fontWeight: 'bold'}}>{receiptNumber}</span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span>{t('receipt_customer')}:</span>
                    <span style={{fontWeight: 'bold'}}>{selectedKasseReceipt.customer_name || '-'}</span>
                  </div>
                </div>

                {/* Items */}
                <div style={{
                  padding: '10px 16px',
                  borderBottom: '2px dashed #9ca3af'
                }}>
                  <div style={{
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    fontSize: '11px',
                    textAlign: 'center'
                  }}>
                    {t('receipt_items_title')}
                  </div>
                  
                  {selectedKasseReceipt.items.map((item, idx) => (
                    <div key={idx} style={{marginBottom: '8px'}}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontWeight: 'bold',
                        marginBottom: '1px'
                      }}>
                        <span>{item.name}</span>
                        <span>€{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                      <div style={{
                        fontSize: '9px',
                        color: '#6b7280',
                        paddingLeft: '4px'
                      }}>
                        {item.quantity} x €{item.price.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div style={{
                  padding: '10px 16px',
                  borderBottom: '2px dashed #9ca3af',
                  fontSize: '10px'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                    <span>{t('receipt_subtotal')}:</span>
                    <span>€{totalWithoutTax.toFixed(2)}</span>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '9px', color: '#6b7280'}}>
                    <span>{t('receipt_tax')}:</span>
                    <span>€{taxAmount.toFixed(2)}</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    paddingTop: '6px',
                    borderTop: '1px solid #9ca3af'
                  }}>
                    <span>{isReturn ? t('receipt_refund') : t('receipt_total')}:</span>
                    <span>€{subtotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* Payment Method */}
                <div style={{
                  padding: '8px 16px',
                  borderBottom: '1px dashed #d1d5db',
                  fontSize: '10px'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span>{t('receipt_payment_method')}:</span>
                    <span style={{fontWeight: 'bold'}}>{t('receipt_payment_cash')}</span>
                  </div>
                </div>

                {/* Thumbnail */}
                {selectedKasseReceipt.thumbnail && (
                  <div style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    backgroundColor: '#f9fafb',
                    borderBottom: '2px dashed #9ca3af'
                  }}>
                    <div style={{
                      fontSize: '9px',
                      color: '#6b7280',
                      marginBottom: '6px'
                    }}>
                      {t('receipt_snapshot')}
                    </div>
                    <img 
                      src={selectedKasseReceipt.thumbnail} 
                      alt="Camera Snapshot"
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: '3px',
                        border: '1px solid #d1d5db'
                      }}
                    />
                  </div>
                )}

                {/* Footer */}
                <div style={{
                  textAlign: 'center',
                  padding: '12px 16px',
                  fontSize: '10px',
                  color: '#6b7280'
                }}>
                  {!isReturn && t('receipt_thank_you')}
                  {isReturn && '↩️ ' + t('receipt_return')}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

    </div>
  );
}

function App() {
  const session = useSession();
  return session.isLoggedIn() ? (
    <Dashboard session={session} />
  ) : (
    <Login onUserLogged={(ut, email) => session.saveUser(ut, email)} />
  );
}

export default App;
// FRESH FILE - Tue Oct  7 19:39:54 UTC 2025
